import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  User, Shield, CreditCard, Repeat, Users, Monitor, Lock, 
  Settings, ChevronRight, Mail, Phone, Globe, Smartphone, 
  Laptop, History, Plus, ExternalLink, ShieldCheck, Key, ArrowRight,
  Camera, RotateCw, Minus, X, CheckCircle, Check, Building2, Wallet, Trash2, Star, ChevronDown, MapPin, Pencil, MapPinPlus,
  Sun, Moon, Palette, SunMoon, Menu, ChevronsUpDown, ChevronUp
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { FaUniversity } from "react-icons/fa";
import { supabase } from "../supabaseClient";
import FozzilPayModal from "./FozzilPayModal";
import FozzilPayCheckout from "./FozzilPayCheckout";
import BillingAddressModal from "./BillingAddressModal";
import DeviceDetailModal from "./DeviceDetailModal";
import FozzilPay from "../utils/FozzilPaySDK";
import { 
    registerDevice, 
    getUserDevices, 
    removeDevice, 
    isDeviceOnline, 
    formatLastActive, 
    getCurrentDeviceId,
    subscribeToSessionChanges,
    checkSessionValidity,
    updateDeviceActivity,
    cleanupDeviceSession,
    trustDevice,
    untrustDevice
} from "../utils/deviceManager";

const countries = [
  { name: "India", code: "+91", flag: "🇮🇳" },
  { name: "United States", code: "+1", flag: "🇺🇸" },
  { name: "United Kingdom", code: "+44", flag: "🇬🇧" },
  { name: "Australia", code: "+61", flag: "🇦🇺" },
  { name: "Canada", code: "+1", flag: "🇨🇦" },
  { name: "Germany", code: "+49", flag: "🇩🇪" },
  { name: "France", code: "+33", flag: "🇫🇷" },
  { name: "Japan", code: "+81", flag: "🇯🇵" },
  { name: "China", code: "+86", flag: "🇨🇳" },
  { name: "Brazil", code: "+55", flag: "🇧🇷" },
  { name: "South Africa", code: "+27", flag: "🇿🇦" },
  { name: "United Arab Emirates", code: "+971", flag: "🇦🇪" },
  { name: "Singapore", code: "+65", flag: "🇸🇬" },
  { name: "Russia", code: "+7", flag: "🇷🇺" },
  { name: "Mexico", code: "+52", flag: "🇲🇽" },
  { name: "Italy", code: "+39", flag: "🇮🇹" },
  { name: "Spain", code: "+34", flag: "🇪🇸" },
  { name: "Netherlands", code: "+31", flag: "🇳🇱" },
  { name: "Sweden", code: "+46", flag: "🇸🇪" },
  { name: "Norway", code: "+47", flag: "🇳🇴" },
  { name: "Denmark", code: "+45", flag: "🇩🇰" },
  { name: "Finland", code: "+358", flag: "🇫🇮" },
  { name: "New Zealand", code: "+64", flag: "🇳🇿" },
  { name: "Ireland", code: "+353", flag: "🇮🇪" },
  { name: "Switzerland", code: "+41", flag: "🇨🇭" },
  { name: "Austria", code: "+43", flag: "🇦🇹" },
  { name: "Belgium", code: "+32", flag: "🇧🇪" },
  { name: "Israel", code: "+972", flag: "🇮🇱" },
  { name: "South Korea", code: "+82", flag: "🇰🇷" },
  { name: "Turkey", code: "+90", flag: "🇹🇷" },
  { name: "Saudi Arabia", code: "+966", flag: "🇸🇦" },
  { name: "Argentina", code: "+54", flag: "🇦🇷" },
  { name: "Chile", code: "+56", flag: "🇨🇱" },
  { name: "Colombia", code: "+57", flag: "🇨🇴" },
  { name: "Peru", code: "+51", flag: "🇵🇪" },
  { name: "Egypt", code: "+20", flag: "🇪🇬" },
  { name: "Thailand", code: "+66", flag: "🇹🇭" },
  { name: "Vietnam", code: "+84", flag: "🇻🇳" },
  { name: "Malaysia", code: "+60", flag: "🇲🇾" },
  { name: "Indonesia", code: "+62", flag: "🇮🇩" },
  { name: "Philippines", code: "+63", flag: "🇵🇭" },
  { name: "Pakistan", code: "+92", flag: "🇵🇰" },
  { name: "Bangladesh", code: "+880", flag: "🇧🇩" },
  { name: "Nigeria", code: "+234", flag: "🇳🇬" },
  { name: "Kenya", code: "+254", flag: "🇰🇪" },
  { name: "Morocco", code: "+212", flag: "🇲🇦" },
  { name: "Portugal", code: "+351", flag: "🇵🇹" },
  { name: "Greece", code: "+30", flag: "🇬🇷" },
  { name: "Poland", code: "+48", flag: "🇵🇱" },
  { name: "Ukraine", code: "+380", flag: "🇺🇦" }
].sort((a, b) => a.name.localeCompare(b.name));

const cleanPhoneNumber = (phone) => {
  if (!phone) return "";
  // Remove all non-numeric characters except +
  let cleaned = phone.replace(/[^\d+]/g, '');
  // If it starts with multiple pluses, fix it
  cleaned = cleaned.replace(/^\++/, '+');
  return cleaned;
};

const parsePhoneParts = (phone, defaultRegion = "India") => {
  if (!phone) {
    const defaultCountry = countries.find(c => c.name === defaultRegion) || countries[0];
    return { code: defaultCountry.code, number: "" };
  }
  
  let cleaned = cleanPhoneNumber(phone);
  
  // Find longest matching country code
  const sortedCountries = [...countries].sort((a, b) => b.code.length - a.code.length);
  for (const c of sortedCountries) {
    if (cleaned.startsWith(c.code)) {
      let num = cleaned.substring(c.code.length);
      // Remove leading zero right after country code
      if (num.startsWith('0')) num = num.substring(1);
      return { code: c.code, number: num };
    }
  }
  
  // Fallback: if no + found, assume it might be a number without code
  const defaultCountry = countries.find(c => c.name === defaultRegion) || countries[0];
  let num = cleaned.replace('+', '');
  if (num.startsWith('0')) num = num.substring(1);
  return { code: defaultCountry.code, number: num };
};

const getCountryFromCode = (code) => countries.find(c => c.code === code) || countries[0];

const formatPhoneDisplay = (phone, region, fallback = "Add phone number") => {
  if (!phone || phone.startsWith('0')) return fallback;
  const parts = parsePhoneParts(phone, region);
  if (!parts.number) return fallback;
  return `${parts.code} ${parts.number}`;
};

const languages = [
  "Afrikaans", "Albanian", "Amharic", "Arabic", "Armenian", "Azerbaijani", "Basque", "Belarusian", "Bengali", "Bosnian", "Bulgarian", "Catalan", "Cebuano", "Chichewa", "Chinese (Simplified)", "Chinese (Traditional)", "Corsican", "Croatian", "Czech", "Danish", "Dutch", "English", "Esperanto", "Estonian", "Filipino", "Finnish", "French", "Frisian", "Galician", "Georgian", "German", "Greek", "Gujarati", "Haitian Creole", "Hausa", "Hawaiian", "Hebrew", "Hindi", "Hmong", "Hungarian", "Icelandic", "Igbo", "Indonesian", "Irish", "Italian", "Japanese", "Javanese", "Kannada", "Kazakh", "Khmer", "Kinyarwanda", "Korean", "Kurdish (Kurmanji)", "Kyrgyz", "Lao", "Latin", "Latvian", "Lithuanian", "Luxembourgish", "Macedonian", "Malagasy", "Malay", "Malayalam", "Maltese", "Maori", "Marathi", "Mongolian", "Myanmar (Burmese)", "Nepali", "Norwegian", "Odia (Oriya)", "Pashto", "Persian", "Polish", "Portuguese", "Punjabi", "Romanian", "Russian", "Samoan", "Scots Gaelic", "Serbian", "Sesotho", "Shona", "Sindhi", "Sinhala", "Slovak", "Slovenian", "Somali", "Spanish", "Sundanese", "Swahili", "Swedish", "Tajik", "Tamil", "Tatar", "Telugu", "Thai", "Turkish", "Turkmen", "Ukrainian", "Urdu", "Uyghur", "Uzbek", "Vietnamese", "Welsh", "Xhosa", "Yiddish", "Yoruba", "Zulu"
].sort();

const CalendarClock = ({ className, size = 24, strokeWidth = 2 }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth={strokeWidth} 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M16 14v2.2l1.6 1" />
    <path d="M16 2v4" />
    <path d="M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.5" />
    <path d="M3 10h5" />
    <path d="M8 2v4" />
    <circle cx="16" cy="16" r="6" />
  </svg>
);

const LanguagesIcon = ({ className, size = 24, strokeWidth = 2 }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth={strokeWidth} 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="m5 8 6 6" />
    <path d="m4 14 6-6 2-3" />
    <path d="M2 5h12" />
    <path d="M7 2h1" />
    <path d="m22 22-5-10-5 10" />
    <path d="M14 18h6" />
  </svg>
);

const PasswordIcon = ({ className, size = 24, strokeWidth = 2 }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth={strokeWidth} 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <rect width="20" height="12" x="2" y="6" rx="2" />
    <path d="M12 12h.01" />
    <path d="M17 12h.01" />
    <path d="M7 12h.01" />
  </svg>
);

const AccountSecurityIcon = ({ className, size = 24, strokeWidth = 2 }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth={strokeWidth} 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <circle cx="10" cy="7" r="4" />
    <path d="M10.3 15H7a4 4 0 0 0-4 4v2" />
    <path d="M15 15.5V14a2 2 0 0 1 4 0v1.5" />
    <rect width="8" height="5" x="13" y="16" rx=".899" />
  </svg>
);

const AccountItem = ({ title, subtitle, icon: Icon, value, theme, onClick }) => {
  const isDark = theme === "dark";
  return (
    <div 
      onClick={onClick}
      className={`relative overflow-hidden ${
        isDark 
          ? 'bg-white/[0.03] border-white/10 hover:border-blue-500/20' 
          : 'bg-white border-blue-100 hover:border-blue-200 shadow-sm'
      } rounded-[32px] p-3 md:p-4 border transition-all duration-300 group cursor-pointer h-full min-h-[100px] md:min-h-[120px]`}
    >
      {/* Blue Background Shade */}
      <div className={`absolute top-0 right-0 w-32 h-32 blur-[40px] pointer-events-none transition-opacity duration-500 ${
        isDark ? 'bg-blue-500/10 opacity-100' : 'bg-blue-600/5 opacity-50 group-hover:opacity-100'
      }`} />
      <div className={`absolute bottom-0 left-0 w-24 h-24 blur-[30px] pointer-events-none transition-opacity duration-500 ${
        isDark ? 'bg-blue-600/5 opacity-100' : 'bg-[#065DC0]/5 opacity-50 group-hover:opacity-100'
      }`} />
      <div className="flex justify-between items-start mb-3">
        <h4 className={`text-[13px] font-semibold ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>{title}</h4>
        <Icon className={`${isDark ? 'text-blue-400 opacity-90' : 'text-blue-500 opacity-70'} w-4 h-4`} />
      </div>
      {subtitle && <p className={`text-[12px] ${isDark ? 'text-[#a1a1a6]' : 'text-[#86868b]'} leading-tight mb-2`}>{subtitle}</p>}
      <p className={`text-[14px] font-medium ${isDark ? 'text-white' : 'text-[#1d1d1f]'} break-all whitespace-pre-line`}>{value}</p>
    </div>
  );
};

const Toggle = ({ checked, onChange, isDark }) => (
  <button 
    type="button"
    onClick={(e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log("Toggle clicked:", !checked);
      onChange(!checked);
    }}
    className={`relative w-[48px] h-[26px] rounded-full transition-all duration-300 focus:outline-none pointer-events-auto cursor-pointer flex-shrink-0 z-20 ${
      checked ? 'bg-blue-500' : (isDark ? 'bg-white/10' : 'bg-gray-200')
    }`}
  >
    <div className={`absolute left-[2px] top-[2px] w-[22px] h-[22px] bg-white rounded-full shadow-lg transition-transform duration-300 ease-in-out ${
      checked ? 'translate-x-[22px]' : 'translate-x-0'
    }`} />
  </button>
);

const SectionHeader = ({ title, subtitle, theme }) => {
  const isDark = theme === "dark";
  return (
    <div className="mb-8">
      <h2 className={`text-[32px] font-bold ${isDark ? 'text-white' : 'text-[#1d1d1f]'} tracking-tight mb-2`}>{title}</h2>
      <p className={`text-[14px] leading-relaxed ${isDark ? 'text-[#a1a1a6]' : 'text-[#515154]'} max-w-[600px]`}>{subtitle}</p>
    </div>
  );
};

const Account = () => {
  // Hook for navigation
  const { user, isAuthenticated, isLoading, logout, notifySecurityChange } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const tabs = [
    { id: "personal", label: "Personal Information", icon: User, path: "/account" },
    { id: "security", label: "Sign-In and Security", icon: Shield, path: "/Sign-In and Security" },
    { id: "payment", label: "Payment and shipping", icon: CreditCard, path: "/Payment and shipping" },
    { id: "subscriptions", label: "Subscriptions", icon: Repeat, path: "/Subscriptions" },
    { id: "devices", label: "Devices", icon: Monitor, path: "/Devices" },
    { id: "appearance", label: "Appearance", icon: SunMoon, path: "/Appearance" },
    { id: "privacy", label: "Privacy", icon: Lock, path: "/Privacy" },
  ];

  const getTabFromPath = (path) => {
    const decodedPath = decodeURIComponent(path);
    const tabMatch = tabs.find(t => t.path === decodedPath);
    if (tabMatch) return tabMatch.id;
    if (decodedPath === "/" || decodedPath === "/account") return "personal";
    return "personal";
  };

  const [activeTab, setActiveTab] = useState(() => getTabFromPath(window.location.pathname));

  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  const [profile, setProfile] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [devices, setDevices] = useState([]);
  const [connectedApps, setConnectedApps] = useState([]);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState({
      allowedMethods: ['Card', 'UPI', 'Netbanking', 'Wallet'],
      amount: "2.00",
      productinfo: "Add Card Verification"
  });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [passwordResetSent, setPasswordResetSent] = useState(false);
  const [passwordResetError, setPasswordResetError] = useState('');
  const [securitySaveMsg, setSecuritySaveMsg] = useState('');
  const [recoverySaveMsg, setRecoverySaveMsg] = useState('');
  const [legacySaveMsg, setLegacySaveMsg] = useState('');
  const [recoveryError, setRecoveryError] = useState('');
  const [legacyError, setLegacyError] = useState('');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null); 
  const [isWheelPickerOpen, setIsWheelPickerOpen] = useState(false);
  const [isCountryWheelPickerOpen, setIsCountryWheelPickerOpen] = useState(false);
  const [isLanguageWheelPickerOpen, setIsLanguageWheelPickerOpen] = useState(false);
  const [tempDateSelection, setTempDateSelection] = useState({ day: 1, month: 'January', year: 2000 });
  const [tempCountrySelection, setTempCountrySelection] = useState("India");
  const [tempLanguageSelection, setTempLanguageSelection] = useState("English");
  const [viewingSub, setViewingSub] = useState(null);
  const [viewingDevice, setViewingDevice] = useState(null);
  const [editData, setEditData] = useState({});
  const [isCountryCodeDropdownOpen, setIsCountryCodeDropdownOpen] = useState(false);
  const [countryCodeSearch, setCountryCodeSearch] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [themePreference, setThemePreference] = useState(() => 
    localStorage.getItem("theme") || "system"
  );

  // Smart Keyboard Navigation for Pickers
  const searchBufferRef = useRef('');
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (!isCountryWheelPickerOpen && !isLanguageWheelPickerOpen && !isWheelPickerOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsCountryWheelPickerOpen(false);
        setIsLanguageWheelPickerOpen(false);
        setIsWheelPickerOpen(false);
        return;
      }
      if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
      
      if (!isCountryWheelPickerOpen && !isLanguageWheelPickerOpen) return;
      
      const char = e.key.toLowerCase();
      searchBufferRef.current += char;
      
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => { searchBufferRef.current = ''; }, 1000);
      
      if (isCountryWheelPickerOpen) {
        const match = countries.map(c => c.name).find(opt => opt.toLowerCase().startsWith(searchBufferRef.current));
        if (match) setTempCountrySelection(match);
      } else if (isLanguageWheelPickerOpen) {
        const match = languages.find(opt => opt.toLowerCase().startsWith(searchBufferRef.current));
        if (match) setTempLanguageSelection(match);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [isCountryWheelPickerOpen, isLanguageWheelPickerOpen, isWheelPickerOpen, countries, languages]);

  // Synchronously determine initial theme match
  const getInitialTheme = () => {
    const pref = localStorage.getItem("theme") || "system";
    if (pref === 'system') {
      return (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) ? 'dark' : 'light';
    }
    return pref;
  };

  const [theme, setTheme] = useState(getInitialTheme());
  // FozzilPay integration
  // FozzilPay integration
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [secondaryAddresses, setSecondaryAddresses] = useState([]);
  const [showFozzilPayModal, setShowFozzilPayModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null); // 'profile' or address object
  const [isDeletingMethod, setIsDeletingMethod] = useState(null);
  
  // Device management
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceReady, setDeviceReady] = useState(false); // Track if device registration is complete
  const [showTrustPrompt, setShowTrustPrompt] = useState(false); // Trust this device prompt
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "FOZZIL Account";
    return () => { document.title = prevTitle; };
  }, []);


  useEffect(() => {
    const applyTheme = () => {
      let appliedTheme = themePreference;
      if (appliedTheme === 'system') {
        appliedTheme = window.matchMedia("(prefers-color-scheme: dark)").matches ? 'dark' : 'light';
      }
      
      if (appliedTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      setTheme(appliedTheme);
    };

    applyTheme();

    if (themePreference === 'system') {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      const handler = () => applyTheme();
      mediaQuery.addEventListener("change", handler);
      return () => mediaQuery.removeEventListener("change", handler);
    }
  }, [themePreference]);

  const handleThemeChange = (newTheme) => {
    setThemePreference(newTheme);
    localStorage.setItem("theme", newTheme);
    // Trigger storage event manually for same-tab listeners (like Header)
    window.dispatchEvent(new StorageEvent('storage', { key: 'theme', newValue: newTheme }));
  };
  
  const isDark = theme === "dark";
  
  const getInitials = () => {
    const firstName = profile?.first_name || user?.user_metadata?.first_name || user?.user_metadata?.full_name?.split(' ')[0] || "";
    const lastName = profile?.last_name || user?.user_metadata?.last_name || user?.user_metadata?.full_name?.split(' ').slice(1).join(' ') || "";
    
    if (firstName && lastName) return (firstName[0] + lastName[0]).toUpperCase();
    if (firstName) return firstName[0].toUpperCase();
    if (user?.email) return user.email[0].toUpperCase();
    return "??";
  };

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/auth');
      return;
    }

    const fetchUserData = async () => {
      if (!user) {
        setIsProfileLoading(false);
        return;
      }
      
      try {
        // Fetch Profile
        const { data: profileData } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .maybeSingle();
        if (profileData) setProfile(profileData);

      // Fetch Subscriptions (Mocked to prevent 404 while table is missing)
      /* const { data: subData } = await supabase
        .from('user_subscriptions')
        .select('*')
        .eq('user_id', user.id);
      if (subData) setSubscriptions(subData); */
      setSubscriptions([]);

      // Register current device and fetch all devices
      try {
        const result = await registerDevice(user.id);
        if (result?.isNewDevice) {
          setShowTrustPrompt(true);
        }
        const deviceData = await getUserDevices(user.id);
        setDevices(deviceData);
        setDeviceReady(true); // Mark device as ready for session monitoring
      } catch (err) {
        console.error('Error with device management:', err);
        setDeviceReady(true); // Still mark as ready to prevent blocking
      }

      // Fetch Connected Apps
      try {
        // Mocking to prevent 404 while table is missing
        /* const { data: appsData } = await supabase
          .from('connected_apps')
          .select('*')
          .eq('user_id', user.id);
        
        if (appsData && appsData.length > 0) {
          setConnectedApps(appsData);
        } else { */
          // Fallback: Default to T.A.E.L.S if no specific data found (User is implicitly connected to Intelligence)
          setConnectedApps([{ name: "T.A.E.L.S" }]);
        // }

      } catch (err) {
        console.error('Error fetching connected apps:', err);
      }
      // Fetch saved payment methods via FozzilPay SDK
      try {
        const methodsResult = await FozzilPay.getPaymentMethods(user.id);
        if (methodsResult.success && methodsResult.data) {
          setPaymentMethods(methodsResult.data);
        }
        
        // Fetch saved addresses
        const addressResult = await FozzilPay.getAddresses(user.id);
        if (addressResult.success && addressResult.data) {
          setSecondaryAddresses(addressResult.data);
        }
      } catch (err) {
        console.error('Error fetching payment/address data:', err);
      }
      } finally {
        setIsProfileLoading(false);
      }
    };

    fetchUserData();
  }, [user, isAuthenticated, isLoading, navigate]);
  
  // Auto-cleanup for malformed phone numbers logic
  useEffect(() => {
    if (profile?.phone && user?.id) {
      const parts = parsePhoneParts(profile.phone, profile.country_region);
      const cleaned = parts.code + parts.number;
      if (cleaned !== profile.phone) {
        const fixProfile = async () => {
          try {
            const { error } = await supabase
              .from('profiles')
              .update({ phone: cleaned })
              .eq('id', user.id);
            if (!error) {
              setProfile(prev => ({ ...prev, phone: cleaned }));
              console.log('Phone number auto-cleaned:', cleaned);
            }
          } catch (err) {
            console.error('Error auto-cleaning phone:', err);
          }
        };
        fixProfile();
      }
    }
  }, [profile?.phone, profile?.country_region, user?.id]);

  // Real-time session monitoring and activity updates
  // Only runs after device is registered to prevent login loops
  useEffect(() => {
    if (!user || !deviceReady) return; // Wait for device registration to complete
    
    // Update activity immediately on mount
    updateDeviceActivity();
    
    // Update activity every 2 minutes
    const activityInterval = setInterval(() => {
      updateDeviceActivity();
    }, 2 * 60 * 1000);
    
    // Subscribe to session changes for remote logout detection
    const deviceId = getCurrentDeviceId();
    let subscription = null;
    
    if (deviceId) {
      subscription = subscribeToSessionChanges(deviceId, async () => {
        // Session was invalidated (remote logout)
        console.warn('Session invalidated remotely');
        await cleanupDeviceSession();
        logout(); // Logout the user
      });
    }
    
    // Periodic session validity check (fallback for real-time)
    // Only runs if device is already registered
    const validityInterval = setInterval(async () => {
      const deviceId = getCurrentDeviceId();
      // Only check if we have a registered device
      if (deviceId) {
        const isValid = await checkSessionValidity();
        if (!isValid) {
          console.warn('Session no longer valid');
          await cleanupDeviceSession();
          logout();
        }
      }
    }, 30 * 1000); // Check every 30 seconds
    
    return () => {
      clearInterval(activityInterval);
      clearInterval(validityInterval);
      if (subscription) {
        supabase.removeChannel(subscription);
      }
    };
  }, [user, logout, deviceReady]);

  // Auto-refresh device list when viewing Devices tab (for real-time online status)
  useEffect(() => {
    if (activeTab !== 'devices' || !user || !deviceReady) return;

    // Refresh device list every 30 seconds while on Devices tab
    const refreshInterval = setInterval(async () => {
      try {
        const deviceData = await getUserDevices(user.id);
        setDevices(deviceData);
      } catch (err) {
        // Silent failure
      }
    }, 30 * 1000);

    return () => clearInterval(refreshInterval);
  }, [activeTab, user, deviceReady]);

  useEffect(() => {
    const isModalOpen = editingItem || showPhotoModal || viewingSub || viewingDevice || 
                        showFozzilPayModal || showCheckoutModal || showBillingModal || showDeviceModal;
    if (isModalOpen) {
      document.body.classList.add('lock-scroll');
    } else {
      document.body.classList.remove('lock-scroll');
    }
    
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setEditingItem(null);
        setShowPhotoModal(false);
        setViewingSub(null);
        setViewingDevice(null);
        setShowDeviceModal(false);
        setSelectedDevice(null);
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => {
      window.removeEventListener("keydown", handleEsc);
      document.body.classList.remove('lock-scroll');
    };
  }, [editingItem, showPhotoModal, viewingSub, viewingDevice, showFozzilPayModal, showCheckoutModal, showBillingModal, showDeviceModal]);

  if (isLoading || !user) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-6 animate-fade-in">
          <div className="relative flex items-center justify-center w-16 h-16">
            <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/40 via-cyan-400/30 to-blue-500/40 blur-xl rounded-full taels-gemini-glow" />
            <div className="absolute -inset-1 rounded-full taels-conic-ring pointer-events-none" />
            <div className="relative z-10 w-11 h-11 rounded-full overflow-hidden flex items-center justify-center bg-black shadow-2xl border border-white/20 taels-gemini-pulse">
              <img src="/taels-logo-black.png" alt="TAELS" className="w-full h-full rounded-full object-cover select-none pointer-events-none" />
            </div>
          </div>
          <div className="flex flex-col items-center gap-1">
            <h1 className="text-white text-lg font-medium tracking-tight">FOZZIL Account</h1>
            <p className="bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-500 bg-clip-text text-transparent text-sm font-medium animate-pulse">Loading your profile...</p>
          </div>
        </div>
      </div>
    );
  }


  const renderContent = () => {
    switch (activeTab) {
      case "personal":
        return (
          <motion.div key="personal" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <SectionHeader 
              title="Personal Information" 
              subtitle="Manage your personal information, including phone numbers and email addresses where you can be contacted."
              theme={theme}
            />
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <AccountItem 
                theme={theme} 
                title="Name" 
                value={profile?.first_name ? `${profile.first_name} ${profile.last_name}` : (user?.user_metadata?.full_name || user?.user_metadata?.first_name || "Not set")} 
                icon={User} 
                onClick={() => {
                  setEditingItem('name');
                  setEditData({ 
                    first_name: profile?.first_name || user?.user_metadata?.first_name || user?.user_metadata?.full_name?.split(' ')[0], 
                    last_name: profile?.last_name || user?.user_metadata?.last_name || user?.user_metadata?.full_name?.split(' ').slice(1).join(' ') 
                  });
                }}
              />
              <AccountItem 
                theme={theme} 
                title="Date of Birth" 
                value={`${profile?.dob_day || ''} ${profile?.dob_month || ''} ${profile?.dob_year || ''}`} 
                icon={CalendarClock} 
                onClick={() => {
                  setEditingItem('dob');
                  setEditData({ dob_day: profile?.dob_day, dob_month: profile?.dob_month, dob_year: profile?.dob_year });
                }}
              />
              <AccountItem 
                theme={theme} 
                title="Country/region" 
                value={profile?.country_region || "India"} 
                icon={Globe} 
                onClick={() => {
                  setEditingItem('country');
                  setEditData({ country_region: profile?.country_region || "India" });
                }}
              />
               <AccountItem 
                 theme={theme} 
                 title="Language" 
                 value={profile?.language || "English (Global)"} 
                 icon={LanguagesIcon} 
                 onClick={() => {
                   setEditingItem('language');
                   setEditData({ language: profile?.language || "English (Global)" });
                 }}
               />
            </div>
          </motion.div>
        );
      case "security":
        return (
          <motion.div key="security" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <SectionHeader 
              title="Sign-In and Security" 
              subtitle="Manage settings related to signing in to your account, account security and how to recover your data when you are having trouble signing in."
              theme={theme}
            />
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <AccountItem 
                theme={theme}
                title="Email & Phone Numbers" 
                value={`${user.email}\n${formatPhoneDisplay(profile?.phone, profile?.country_region)}`} 
                icon={Mail} 
                onClick={() => {
                  setEditingItem('email_phone');
                  const parts = parsePhoneParts(profile?.phone, profile?.country_region || "India");
                  setEditData({ 
                    email: user.email, 
                    phone: profile?.phone,
                    showPhoneInput: !profile?.phone || profile?.phone?.startsWith('0'),
                    isPhoneLocked: !!profile?.phone && !profile?.phone?.startsWith('0'),
                    phoneCode: parts.code,
                    phoneNumber: parts.number
                  });
                }}
              />
              <AccountItem 
                theme={theme}
                title="Password" 
                subtitle="Change your password regularly to keep your FOZZIL ID secure."
                value="••••••••••••" 
                icon={PasswordIcon} 
                onClick={() => setEditingItem('password')}
              />
              <AccountItem 
                theme={theme}
                title="Account Security" 
                subtitle="Two-factor authentication adds an extra layer of security."
                value={profile?.two_factor_auth ? "Two-factor authentication • ON" : "Two-factor authentication • OFF"} 
                icon={AccountSecurityIcon} 
                onClick={() => {
                  setEditingItem('security');
                  setEditData({ two_factor_auth: profile?.two_factor_auth });
                }}
              />
              <AccountItem 
                theme={theme}
                title="Account Recovery" 
                subtitle="Manage how to recover your account if you forget your password."
                value={profile?.recovery_contact ? `✓ ${profile.recovery_contact}` : "Set Up Recovery"} 
                icon={Shield} 
                onClick={() => {
                  setEditingItem('recovery');
                  setRecoveryError(''); setRecoverySaveMsg('');
                  setEditData({ 
                    recovery_setup: profile?.recovery_setup, 
                    recovery_contact: profile?.recovery_contact || '',
                    _recoveryContactSaved: !!profile?.recovery_contact 
                  });
                }}
              />
               <AccountItem 
                 theme={theme}
                 title="Legacy Contact" 
                 subtitle="Designate someone to have access to your account data after your death."
                 value={profile?.legacy_contact || "Not Set Up"} 
                 icon={Users} 
                 onClick={() => {
                   setEditingItem('legacy');
                   setLegacyError(''); setLegacySaveMsg('');
                   setEditData({ 
                     legacy_contact: profile?.legacy_contact || '',
                     _legacyContactSaved: !!profile?.legacy_contact 
                   });
                 }}
               />
               <AccountItem 
                 theme={theme}
                 title="Sign In with FOZZIL" 
                 subtitle="Manage the apps and websites where you use your FOZZIL ID to sign in."
                 value={`${connectedApps.length} apps and websites`} 
                 icon={Monitor} 
                 onClick={() => {
                  setEditingItem('signin');
                 }}
               />
               <AccountItem 
                 theme={theme}
                 title="App-Specific Passwords" 
                 subtitle="Sign in to apps and services not provided by FOZZIL."
                 value={profile?.app_passwords ? "Active" : "No passwords"} 
                 icon={Key} 
                 onClick={() => {
                  setEditingItem('app_passwords');
                  setEditData({ app_passwords: profile?.app_passwords });
                 }}
               />
            </div>
          </motion.div>
        );
      case "payment":
        const handleDeletePaymentMethod = async (methodId) => {
          setIsDeletingMethod(methodId);
          try {
            // Check if deleting primary
            const methodToDelete = paymentMethods.find(m => m.id === methodId);
            const isDeletingPrimary = methodToDelete?.is_primary;

            const result = await FozzilPay.deletePaymentMethod(methodId);
            if (result.success) {
              setPaymentMethods(prev => {
                const remaining = prev.filter(m => m.id !== methodId);
                // If we deleted the primary method and there are other methods left, 
                // typically the backend might auto-assign or we need to prevent deleting primary without reassignment.
                // Assuming user wants auto-assign:
                if (isDeletingPrimary && remaining.length > 0) {
                   // Optimistically set the first one as primary
                   // ideally we should call API to set primary, but let's assume backend might handle or we prompt user
                   // For this task: "if primary method deleted then auto select the next one"
                   // We'll just update UI state to reflect this, but effectively we'd need another API call 
                   // or check if backend did it. To be safe, let's trigger a setPrimary on the first one remaining.
                   handleSetPrimary(remaining[0].id); // Trigger API
                }
                return remaining;
              });
            }
          } catch (err) {
            console.error('Error deleting method:', err);
          } finally {
            setIsDeletingMethod(null);
          }
        };

        const handleSetPrimary = async (methodId) => {
          try {
            const result = await FozzilPay.setPrimaryPaymentMethod(methodId, user.id);
            if (result.success) {
              setPaymentMethods(prev => prev.map(m => ({
                ...m,
                is_primary: m.id === methodId
              })));
            }
          } catch (err) {
            console.error('Error setting primary:', err);
          }
        };

        const getPaymentIcon = (type) => {
          switch (type) {
            case 'UPI': 
              return <img src="/fpay-logos/upi-logo.png" alt="UPI" className="h-5 object-contain" />;
            case 'Card': 
              return (
                 <div className="w-8 h-5 rounded bg-gradient-to-br from-gray-400 via-gray-200 to-gray-500 shadow-sm flex items-center justify-center border border-gray-400/50">
                    <div className="w-full h-[1px] bg-black/10 mt-1"></div>
                 </div>
              );
            case 'Net Banking': return <FaUniversity className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />;
            case 'Wallet': return <Wallet className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />;
            default: return <CreditCard className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />;
          }
        };

        return (
          <motion.div key="payment" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <SectionHeader 
              title="Payment & Billing" 
              subtitle="Manage your payment methods and billing addresses."
              theme={theme}
            />

            {/* Payment Methods Section */}
            <div className="mb-8">
              <h4 className={`text-[14px] font-bold mb-4 ${isDark ? 'text-white' : 'text-black'}`}>Payment Methods</h4>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {paymentMethods.length > 0 && paymentMethods.map((method) => (
                  <div 
                    key={method.id}
                    className={`relative overflow-hidden ${
                      isDark 
                        ? 'bg-white/[0.03] border-white/10 shadow-2xl shadow-blue-950/20 hover:border-blue-500/30' 
                        : 'bg-white/70 border-blue-100 shadow-xl shadow-blue-500/5 hover:border-blue-200'
                    } backdrop-blur-[40px] rounded-[32px] p-3 md:p-4 border transition-all duration-700 group cursor-pointer h-full min-h-[100px] md:min-h-[120px] relative flex flex-col`}
                  >
                    {/* Blue Background Shade */}
                    <div className={`absolute top-0 right-0 w-32 h-32 blur-[60px] pointer-events-none transition-opacity duration-700 ${
                      isDark ? 'bg-blue-500/15 opacity-100' : 'bg-blue-600/5 opacity-50 group-hover:opacity-100'
                    }`} />
                    <div className={`absolute bottom-0 left-0 w-24 h-24 blur-[40px] pointer-events-none transition-opacity duration-700 ${
                      isDark ? 'bg-blue-600/10 opacity-100' : 'bg-[#065DC0]/5 opacity-50 group-hover:opacity-100'
                    }`} />
                    <div className="flex justify-between items-start mb-3 relative">
                      <h4 className={`text-[13px] font-semibold ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>{method.display_name}</h4>
                      
                      {/* Action Icons - Absolute Top Right */}
                      {/* Action Icons - Absolute Top Right */}
                      <div className="absolute top-0 right-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          {!method.is_primary && (
                            <button
                              onClick={(e) => { e.stopPropagation(); handleSetPrimary(method.id); }}
                              className="flex items-center gap-1.5 px-2 py-1 rounded-md text-blue-500 hover:text-blue-400 transition-colors text-[11px] font-semibold whitespace-nowrap"
                              title="Set as Primary"
                            >
                              Set as Primary
                            </button>
                          )}
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeletePaymentMethod(method.id); }}
                            disabled={isDeletingMethod === method.id}
                            className={`p-1.5 rounded-md ${isDark ? 'text-gray-400 hover:text-red-500 hover:bg-white/10' : 'text-gray-400 hover:text-red-600 hover:bg-gray-100'} transition-colors disabled:opacity-50`}
                            title="Remove"
                          >
                            {isDeletingMethod === method.id ? (
                              <div className={`w-3.5 h-3.5 border-2 ${isDark ? 'border-white' : 'border-gray-600'} border-t-transparent rounded-full animate-spin`} />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                      </div>
                    </div>
                    
                    <div className="mt-auto flex justify-between items-end">
                      <div>
                        <p className={`text-[12px] ${isDark ? 'text-[#a1a1a6]' : 'text-[#86868b]'} leading-tight mb-2 uppercase tracking-wide opacity-70`}>{method.payment_type}</p>
                        {method.is_primary && (
                           <span className="inline-flex items-center gap-1 text-[10px] font-bold text-blue-500 bg-blue-500/10 px-2 py-0.5 rounded-full">Primary</span>
                        )}
                      </div>
                      {getPaymentIcon(method.payment_type)}
                    </div>
                  </div>
                ))}
                
                {/* Add Payment Method - Text Only Button */}
                <div 
                   onClick={() => setShowFozzilPayModal(true)}
                   className={`relative overflow-hidden h-full min-h-[100px] md:min-h-[120px] flex flex-col items-center justify-center gap-2 text-blue-500 transition-all duration-700 cursor-pointer border rounded-[32px] border-dashed backdrop-blur-[40px] ${
                     isDark 
                       ? 'bg-white/[0.03] border-white/10 hover:border-blue-500/30 shadow-2xl shadow-blue-950/20' 
                       : 'bg-white/70 border-blue-100 hover:border-blue-200 shadow-xl shadow-blue-500/5'
                   } group`}
                 >
                    {/* Blue Background Shade */}
                    <div className={`absolute top-0 right-0 w-24 h-24 blur-[40px] pointer-events-none transition-opacity duration-700 ${
                      isDark ? 'bg-blue-500/15 opacity-100' : 'bg-blue-600/5 opacity-50 group-hover:opacity-100'
                    }`} />
                    <div className={`absolute bottom-0 left-0 w-16 h-16 blur-[30px] pointer-events-none transition-opacity duration-700 ${
                      isDark ? 'bg-blue-600/10 opacity-100' : 'bg-[#065DC0]/5 opacity-50 group-hover:opacity-100'
                    }`} />
                    <Plus size={24} className="relative z-10" />
                    <span className="font-semibold text-sm relative z-10">Add Payment Method</span>
                </div>
              </div>
            </div>

            {/* Billing Address Section */}
            <div className="mb-6">
              <h4 className={`text-[14px] font-bold mb-4 ${isDark ? 'text-white' : 'text-black'}`}>Billing Address</h4>
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {/* 1. Primary Profile Address */}
                <div 
                  className={`relative overflow-hidden ${
                    isDark 
                      ? 'bg-white/[0.03] border-white/10 shadow-2xl shadow-blue-950/20 hover:border-blue-500/30' 
                      : 'bg-white/70 border-blue-100 shadow-xl shadow-blue-500/5 hover:border-blue-200'
                  } backdrop-blur-[40px] rounded-[32px] p-3 md:p-4 border transition-all duration-700 group h-full min-h-[100px] md:min-h-[120px] relative`}
                >
                  {/* Blue Background Shade */}
                  <div className={`absolute top-0 right-0 w-24 h-24 blur-[40px] pointer-events-none transition-opacity duration-700 ${
                    isDark ? 'bg-blue-500/15 opacity-100' : 'bg-blue-600/5 opacity-50 group-hover:opacity-100'
                  }`} />
                  <div className={`absolute bottom-0 left-0 w-16 h-16 blur-[30px] pointer-events-none transition-opacity duration-700 ${
                    isDark ? 'bg-blue-600/10 opacity-100' : 'bg-[#065DC0]/5 opacity-50 group-hover:opacity-100'
                  }`} />
                  <div className="flex justify-between items-start mb-3">
                    <h4 className={`text-[13px] font-semibold ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>
                      {profile?.address_line ? "Billing Address (Primary)" : "Billing Address"}
                    </h4>
                    <MapPin className={`${isDark ? 'text-blue-400 opacity-90' : 'text-blue-500 opacity-70'} w-4 h-4`} />
                  </div>
                  {profile?.address_line ? (
                    <div className="flex flex-col gap-0.5">
                        <p className={`text-[14px] font-medium ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>{profile?.first_name} {profile?.last_name}</p>
                        <p className={`text-[12px] ${isDark ? 'text-[#a1a1a6]' : 'text-[#86868b]'} leading-tight`}>{profile?.address_line}, {profile?.city}</p>
                        <p className={`text-[12px] ${isDark ? 'text-[#a1a1a6]' : 'text-[#86868b]'} leading-tight`}>{profile?.state} {profile?.pincode}</p>
                        <p className={`text-[12px] ${isDark ? 'text-[#a1a1a6]' : 'text-[#86868b]'} leading-tight`}>{profile?.country_region}</p>
                    </div>
                  ) : (
                     <div className="h-[60px] flex items-center">
                        <p className="text-[14px] font-medium text-gray-500 italic">Not Set Up</p>
                     </div>
                  )}
                  <div className="absolute bottom-4 right-4">
                     <button
                        onClick={() => {
                            setEditingAddress('profile');
                            setShowBillingModal(true);
                        }} 
                        className={`text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity ${isDark ? 'text-blue-400 hover:text-blue-300' : 'text-blue-500 hover:text-blue-600'}`}
                     >
                       Edit
                     </button>
                  </div>
                </div>

                {/* 2. Secondary Addresses */}
                {secondaryAddresses.map((addr) => (
                    <div 
                      key={addr.id}
                      className={`relative overflow-hidden ${
                        isDark 
                          ? 'bg-white/[0.03] border-white/10 shadow-2xl shadow-blue-950/20 hover:border-blue-500/30' 
                          : 'bg-white/70 border-blue-100 shadow-xl shadow-blue-500/5 hover:border-blue-200'
                      } backdrop-blur-[40px] rounded-[32px] p-3 md:p-4 border transition-all duration-700 group h-full min-h-[100px] md:min-h-[120px] relative`}
                    >
                      {/* Blue Background Shade */}
                      <div className={`absolute top-0 right-0 w-24 h-24 blur-[40px] pointer-events-none transition-opacity duration-700 ${
                        isDark ? 'bg-blue-500/15 opacity-100' : 'bg-blue-600/5 opacity-50 group-hover:opacity-100'
                      }`} />
                      <div className={`absolute bottom-0 left-0 w-16 h-16 blur-[30px] pointer-events-none transition-opacity duration-700 ${
                        isDark ? 'bg-blue-600/10 opacity-100' : 'bg-[#065DC0]/5 opacity-50 group-hover:opacity-100'
                      }`} />
                      <div className="flex justify-between items-start mb-3">
                        <h4 className={`text-[13px] font-semibold ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>Billing Address</h4>
                        <MapPin className={`${isDark ? 'text-gray-400 opacity-90' : 'text-gray-500 opacity-70'} w-4 h-4`} />
                      </div>
                      <div className="flex flex-col gap-0.5">
                          <p className={`text-[14px] font-medium ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>{addr.first_name} {addr.last_name}</p>
                          <p className={`text-[12px] ${isDark ? 'text-[#a1a1a6]' : 'text-[#86868b]'} leading-tight`}>{addr.address_line}, {addr.city}</p>
                          <p className={`text-[12px] ${isDark ? 'text-[#a1a1a6]' : 'text-[#86868b]'} leading-tight`}>{addr.state} {addr.pincode}</p>
                          <p className={`text-[12px] ${isDark ? 'text-[#a1a1a6]' : 'text-[#86868b]'} leading-tight`}>{addr.country_region}</p>
                      </div>
                       <div className="absolute bottom-4 right-4 flex gap-3">
                         <button
                            onClick={async () => {
                                if (window.confirm("Delete this address?")) {
                                    await FozzilPay.deleteAddress(addr.id);
                                    setSecondaryAddresses(prev => prev.filter(p => p.id !== addr.id));
                                }
                            }} 
                            className={`text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-400`}
                         >
                           Delete
                         </button>
                      </div>
                    </div>
                ))}

                {/* 3. Add New Address (Limit Check) */}
                {((profile?.address_line ? 1 : 0) + secondaryAddresses.length) < 5 ? (
                    <div 
                       onClick={() => {
                            setEditingAddress(null); // Indicates 'Add New'
                            setShowBillingModal(true);
                       }}
                       className={`relative overflow-hidden h-full min-h-[120px] flex flex-col items-center justify-center gap-2 text-blue-500 transition-all duration-700 cursor-pointer border rounded-[32px] border-dashed backdrop-blur-[40px] ${
                         isDark 
                           ? 'bg-white/[0.03] border-white/10 hover:border-blue-500/30 shadow-2xl shadow-blue-950/20' 
                           : 'bg-white/70 border-blue-100 hover:border-blue-200 shadow-xl shadow-blue-500/5'
                       } group`}
                     >
                        {/* Blue Background Shade */}
                        <div className={`absolute top-0 right-0 w-24 h-24 blur-[40px] pointer-events-none transition-opacity duration-700 ${
                          isDark ? 'bg-blue-500/15 opacity-100' : 'bg-blue-600/5 opacity-50 group-hover:opacity-100'
                        }`} />
                        <div className={`absolute bottom-0 left-0 w-16 h-16 blur-[30px] pointer-events-none transition-opacity duration-700 ${
                          isDark ? 'bg-blue-600/10 opacity-100' : 'bg-[#065DC0]/5 opacity-50 group-hover:opacity-100'
                        }`} />
                        <MapPinPlus size={24} className="relative z-10" />
                        <span className="font-semibold text-sm relative z-10">Add New Address</span>
                    </div>
                ) : (
                    <div 
                       className={`relative overflow-hidden h-full min-h-[120px] flex flex-col items-center justify-center gap-2 text-gray-400 border rounded-[32px] border-dashed backdrop-blur-[40px] ${
                         isDark 
                           ? 'bg-white/[0.03] border-white/5 opacity-50' 
                           : 'bg-white/50 border-gray-100 opacity-50'
                       } cursor-not-allowed`}
                       title="Address limit reached (5)"
                     >
                        <MapPinPlus size={24} />
                        <span className="font-semibold text-sm">Limit Reached</span>
                    </div>
                )}
              </div>
            </div>
          </motion.div>
        );
      case "subscriptions":
        if (viewingSub) {
          const sub = viewingSub;
          const isActive = sub.status === 'Active';
          return (
            <motion.div key="subscriptions-detail" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-8">
              <button onClick={() => setViewingSub(null)} className="flex items-center gap-1 text-blue-500 font-medium hover:underline mb-4">
                <ChevronRight className="rotate-180" size={16} /> Subscriptions
              </button>
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 ${isDark ? 'bg-white/5' : 'bg-blue-50'} rounded-[32px] flex items-center justify-center overflow-hidden`}>
                  {sub.icon_url ? <img src={sub.icon_url} alt={sub.name} className="w-10 h-10" /> : <Repeat size={32} className="text-blue-500" />}
                </div>
                <h2 className={`text-[32px] font-bold ${isDark ? 'text-white' : 'text-black'}`}>{sub.name}</h2>
              </div>
              <div className="space-y-6 pt-4">
                <h3 className={`text-[20px] font-bold ${isDark ? 'text-white' : 'text-black'}`}>{sub.plan}</h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <CreditCard size={20} className="text-gray-400" />
                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>₹ {sub.price} per month</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <History size={20} className="text-gray-400" />
                    <p className={`${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                      {isActive ? `Renews ${new Date(sub.renews_at).toLocaleDateString()}` : `Expired ${new Date(sub.expired_at).toLocaleDateString()}`}
                    </p>
                  </div>
                </div>
                {isActive ? (
                  <div className={`mt-8 p-6 rounded-[32px] ${isDark ? 'bg-white/03 border-white/10' : 'bg-white/70 border-blue-100'} backdrop-blur-[40px] border shadow-2xl`}>
                    <h4 className="font-bold mb-2">Manage Subscription</h4>
                    <p className="text-sm text-gray-500 mb-4">You can use your device to manage this subscription.</p>
                    <button className="flex items-center gap-2 text-blue-500 font-bold text-sm">
                      <Smartphone size={16} /> Continue on Device...
                    </button>
                  </div>
                ) : (
                  <div className="pt-2">
                    <p className="text-red-600 font-bold text-[17px]">This subscription is inactive.</p>
                    <div className="flex flex-col gap-4 max-w-[200px] pt-4">
                      <button className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg font-bold">Renew: ₹ {sub.price}/month</button>
                    </div>
                  </div>
                )}
                <button className="text-blue-500 text-[14px] font-medium hover:underline pt-8">About Subscriptions and Privacy</button>
              </div>
            </motion.div>
          );
        }
        return (
          <motion.div key="subscriptions" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <SectionHeader 
              title="Subscriptions" 
              subtitle="Manage your FOZZIL Ecosystem subscriptions."
              theme={theme}
            />
            <div className="space-y-10">
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {subscriptions.map(sub => (
                  <div 
                    key={sub.id}
                    onClick={() => setViewingSub(sub)}
                    className={`relative overflow-hidden ${
                      isDark 
                        ? 'bg-white/[0.03] border-white/10 shadow-2xl shadow-blue-950/20 hover:border-blue-500/30' 
                        : 'bg-white/70 border-blue-100 shadow-xl shadow-blue-500/5 hover:border-blue-200'
                    } backdrop-blur-[40px] rounded-[32px] p-3 md:p-4 border flex flex-col justify-between cursor-pointer transition-all duration-700 min-h-[120px] group`}
                  >
                    {/* Blue Background Shade */}
                    <div className={`absolute top-0 right-0 w-32 h-32 blur-[60px] pointer-events-none transition-opacity duration-700 ${
                      isDark ? 'bg-blue-500/15 opacity-100' : 'bg-blue-600/5 opacity-50 group-hover:opacity-100'
                    }`} />
                    <div className={`absolute bottom-0 left-0 w-24 h-24 blur-[40px] pointer-events-none transition-opacity duration-700 ${
                      isDark ? 'bg-blue-600/10 opacity-100' : 'bg-[#065DC0]/5 opacity-50 group-hover:opacity-100'
                    }`} />
                    <div className="flex items-center gap-4 relative z-10">
                      <div className={`w-14 h-14 ${isDark ? 'bg-white/5' : 'bg-blue-50'} rounded-xl flex items-center justify-center`}>
                        {sub.icon_url ? <img src={sub.icon_url} alt={sub.name} className="w-8 h-8" /> : <Repeat size={24} className="text-blue-500" />}
                      </div>
                      <div>
                        <p className={`font-bold ${isDark ? 'text-white' : 'text-black'} text-[17px]`}>{sub.name}</p>
                        <p className={`text-sm ${isDark ? 'text-[#a1a1a6]' : 'text-gray-500'}`}>{sub.plan} • {sub.status}</p>
                      </div>
                    </div>
                  </div>
                ))}
                {subscriptions.length === 0 && (
                  <div className="col-span-2 text-center py-10 opacity-50">No active subscriptions found.</div>
                )}
              </div>

              <div className="pt-6 border-t border-gray-100 dark:border-white/5 relative z-10">
                <div className="flex items-center justify-between py-2">
                  <div>
                    <p className={`font-bold ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>Renewal Receipt Emails</p>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} mt-1 max-w-[500px]`}>A receipt will be sent to you each time one of your subscriptions renews.</p>
                  </div>
                  <Toggle 
                    isDark={isDark} 
                    checked={profile?.renewal_emails ?? true} 
                    onChange={async (val) => {
                      console.log("Setting Renewal Emails to:", val);
                      setProfile(prev => ({...prev, renewal_emails: val}));
                      try {
                        const { error } = await supabase.from('profiles').update({ renewal_emails: val }).eq('id', user.id);
                        if (error) {
                          console.error("Supabase Renewal Emails Error:", error);
                        }
                      } catch (err) {
                        console.error("Failed to update renewal emails preference:", err);
                      }
                    }} 
                  />
                </div>
              </div>
            </div>
          </motion.div>
        );
      case "devices":
        return (
          <motion.div key="devices" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <SectionHeader 
              title="Devices" 
              subtitle="See details for the devices associated with your FOZZIL ID."
              theme={theme}
            />
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              {devices.length > 0 ? (
                devices.map(device => {
                  const online = isDeviceOnline(device.last_active);
                  const currentDeviceId = getCurrentDeviceId();
                  const isCurrentDevice = device.id === currentDeviceId;
                  const deviceIcon = device.device_type === 'mobile' || device.device_type === 'tablet' 
                    ? <Smartphone className="w-5 h-5" /> 
                    : <Laptop className="w-5 h-5" />;
                  
                  return (
                    <div 
                      key={device.id}
                      onClick={() => {
                        setSelectedDevice(device);
                        setShowDeviceModal(true);
                      }}
                      className={`relative overflow-hidden ${
                        isDark 
                          ? 'bg-white/[0.03] border-white/10 shadow-2xl shadow-blue-950/20 hover:border-blue-500/30' 
                          : 'bg-white/70 border-blue-100 shadow-xl shadow-blue-500/5 hover:border-blue-200'
                      } backdrop-blur-[40px] rounded-[32px] p-3 md:p-5 border transition-all duration-700 group cursor-pointer min-h-[100px] md:min-h-[120px]`}
                    >
                      {/* Blue Background Shade */}
                      <div className={`absolute top-0 right-0 w-32 h-32 blur-[60px] pointer-events-none transition-opacity duration-700 ${
                        isDark ? 'bg-blue-500/15 opacity-100' : 'bg-blue-600/5 opacity-50 group-hover:opacity-100'
                      }`} />
                      <div className={`absolute bottom-0 left-0 w-24 h-24 blur-[40px] pointer-events-none transition-opacity duration-700 ${
                        isDark ? 'bg-blue-600/10 opacity-100' : 'bg-[#065DC0]/5 opacity-50 group-hover:opacity-100'
                      }`} />
                      {/* Header with label and badge */}
                      <div className="flex items-center justify-between mb-3">
                        <span className={`text-[11px] font-medium uppercase tracking-wide ${isDark ? 'text-[#86868b]' : 'text-gray-400'}`}>
                          {device.device_type === 'mobile' ? 'Mobile' : device.device_type === 'tablet' ? 'Tablet' : 'Computer'}
                        </span>
                        {isCurrentDevice && (
                          <span className="text-[10px] font-bold uppercase tracking-wider text-blue-500 bg-blue-500/10 px-2 py-0.5 rounded-full">
                            This Device
                          </span>
                        )}
                      </div>
                      
                      {/* Device Name */}
                      <div className="flex items-center gap-3 mb-2">
                        <div className="relative">
                          <div className={`p-2 rounded-lg ${isDark ? 'bg-white/5' : 'bg-gray-100'} ${online ? 'text-blue-500' : isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {deviceIcon}
                          </div>
                          <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 ${isDark ? 'border-[#1c1c1e]' : 'border-white'} ${online ? 'bg-green-500' : 'bg-gray-400'}`} />
                        </div>
                        <div>
                          <h4 className={`font-bold text-[17px] ${isDark ? 'text-white' : 'text-black'}`}>
                            {device.device_name}
                          </h4>
                          <p className={`text-[13px] ${isDark ? 'text-[#a1a1a6]' : 'text-gray-500'}`}>
                            {device.os} {device.os_version}
                          </p>
                        </div>
                      </div>
                      
                      {/* Status */}
                      <p className={`text-[12px] ${online ? 'text-green-500' : isDark ? 'text-[#86868b]' : 'text-gray-400'}`}>
                        {online ? 'Online now' : `Last active: ${formatLastActive(device.last_active)}`}
                      </p>
                    </div>
                  );
                })
              ) : (
                <div className="col-span-2 text-center py-10">
                  <Laptop className={`w-12 h-12 mx-auto mb-3 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
                  <p className={`${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No devices associated with this account.</p>
                  <p className={`text-sm ${isDark ? 'text-gray-600' : 'text-gray-400'} mt-1`}>Devices will appear here when you sign in.</p>
                </div>
              )}
            </div>
          </motion.div>
        );
      case "appearance":
        return (
          <motion.div key="appearance" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <SectionHeader 
              title="Appearance" 
              subtitle="Customize the look and feel of your FOZZIL ecosystem."
              theme={theme}
            />
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-3 md:gap-4">
                {[
                  { id: 'light', label: 'Light', icon: Sun },
                  { id: 'dark', label: 'Dark', icon: Moon },
                  { id: 'system', label: 'System', icon: Monitor },
                ].map((item) => {
                  const Icon = item.icon;
                  const isSelected = themePreference === item.id;
                  
                  return (
                    <div 
                      key={item.id}
                      onClick={() => handleThemeChange(item.id)}
                      className={`relative overflow-hidden ${
                        isSelected
                          ? (isDark ? 'bg-white/[0.08] border-blue-500 shadow-2xl shadow-blue-950/20' : 'bg-white border-blue-500 shadow-xl shadow-blue-500/10')
                          : (isDark ? 'bg-white/[0.03] border-white/10 hover:bg-white/[0.05] hover:border-blue-500/30' : 'bg-white/70 border-blue-100 hover:border-blue-200 shadow-xl shadow-blue-500/5')
                      } backdrop-blur-[40px] rounded-[32px] p-4 md:p-5 border transition-all duration-700 cursor-pointer h-full min-h-[120px] group`}
                    >
                      {/* Blue Background Shade */}
                      <div className={`absolute top-0 right-0 w-32 h-32 blur-[60px] pointer-events-none transition-opacity duration-700 ${
                        isDark ? 'bg-blue-500/15 opacity-100' : 'bg-blue-600/5 opacity-50 group-hover:opacity-100'
                      }`} />
                      <div className={`absolute bottom-0 left-0 w-24 h-24 blur-[40px] pointer-events-none transition-opacity duration-700 ${
                        isDark ? 'bg-blue-600/10 opacity-100' : 'bg-[#065DC0]/5 opacity-50 group-hover:opacity-100'
                      }`} />

                      <div className="relative z-10 flex flex-col justify-between h-full">
                        <div className="flex items-center justify-between">
                          <Icon size={24} className={isSelected ? 'text-blue-500' : (isDark ? 'text-gray-400' : 'text-gray-500')} />
                          {isSelected && <div className="w-2 h-2 rounded-full bg-blue-500 shadow-[0_0_10px_#3b82f6]" />}
                        </div>
                        <div>
                          <p className={`font-bold ${isDark ? 'text-white' : 'text-black'} text-[17px]`}>{item.label}</p>
                          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item.id === 'system' ? 'Follow device settings' : `${item.label} Mode`}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className={`mt-4 text-[13px] ${isDark ? 'text-[#a1a1a6]' : 'text-gray-500'} leading-relaxed`}>
                {themePreference === 'system' 
                  ? "System theme will automatically adjust the appearance based on your device's settings." 
                  : `Always use ${themePreference} mode throughout the FOZZIL ecosystem.`}
              </p>
            </div>
          </motion.div>
        );
      case "privacy":
        return (
          <motion.div key="privacy" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <SectionHeader 
              title="Privacy" 
              subtitle="Control how your data is used across FOZZIL services."
              theme={theme}
            />
            <div className="space-y-6">
              <div className={`relative overflow-hidden ${
                isDark 
                  ? 'bg-white/[0.03] border-white/10 shadow-2xl shadow-blue-950/20' 
                  : 'bg-white/70 border-blue-100 shadow-xl shadow-blue-500/5'
              } backdrop-blur-[40px] rounded-[32px] p-6 border transition-all duration-700 group`}>
                {/* Blue Background Shade */}
                <div className={`absolute top-0 right-0 w-32 h-32 blur-[60px] pointer-events-none transition-opacity duration-700 ${
                  isDark ? 'bg-blue-500/15 opacity-100' : 'bg-blue-600/5 opacity-50 group-hover:opacity-100'
                }`} />
                <div className={`absolute bottom-0 left-0 w-24 h-24 blur-[40px] pointer-events-none transition-opacity duration-700 ${
                  isDark ? 'bg-blue-600/10 opacity-100' : 'bg-[#065DC0]/5 opacity-50 group-hover:opacity-100'
                }`} />
                <h4 className={`text-[17px] font-bold mb-4 ${isDark ? 'text-white' : 'text-black'} relative z-10`}>Data and Privacy</h4>
                <div className="space-y-4 relative z-10">
                  <div className="flex items-center justify-between py-2 border-b border-white/5">
                    <div>
                      <p className={`font-semibold ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>Personalized Recommendations</p>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Allow FOZZIL to use your activity to recommend products.</p>
                    </div>
                    <Toggle 
                      isDark={isDark}
                      checked={profile?.privacy_recommendations ?? true}
                      onChange={async (val) => {
                        console.log("Setting Privacy Recommendations to:", val);
                        setProfile(prev => ({...prev, privacy_recommendations: val}));
                        try {
                          const { error } = await supabase.from('profiles').update({ privacy_recommendations: val }).eq('id', user.id);
                          if (error) {
                            console.error("Supabase Privacy Recommendations Error:", error);
                            // We don't revert immediately to give better UX, or we can Toast here
                          }
                        } catch (err) {
                          console.error("Failed to update privacy recommendations:", err);
                        }
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <div>
                      <p className={`font-semibold ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>Share Analytics</p>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Help improve FOZZIL products by sharing usage data.</p>
                    </div>
                    <Toggle 
                      isDark={isDark}
                      checked={profile?.privacy_analytics ?? true}
                      onChange={async (val) => {
                        console.log("Setting Privacy Analytics to:", val);
                        setProfile(prev => ({...prev, privacy_analytics: val}));
                        try {
                          const { error } = await supabase.from('profiles').update({ privacy_analytics: val }).eq('id', user.id);
                          if (error) {
                            console.error("Supabase Privacy Analytics Error:", error);
                          }
                        } catch (err) {
                          console.error("Failed to update privacy analytics:", err);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-black' : 'bg-[#F5F5F7]'} font-sans selection:bg-blue-500/20 transition-colors duration-500`}>
      {/* Top Breadcrumb Header */}
      <div className="sticky top-0 z-[9999]">
        <div className={`${isDark ? 'bg-black border-white/10' : 'bg-white border-gray-200'} border-b py-3 px-6 md:px-12 flex justify-between items-center isolate`}>
          <div className="flex items-center gap-3">
            <div className={`overflow-hidden rounded-xl bg-gradient-to-b ${isDark ? 'from-white/10 to-transparent shadow-[0_0_15px_rgba(59,130,246,0.5)]' : 'from-black/[0.05] to-transparent shadow-[0_0_15px_rgba(59,130,246,0.3)]'} backdrop-blur-sm flex items-center justify-center w-8 h-8 md:w-9 md:h-9 transition-colors duration-500`}>
              <img src="/logoicon.png" alt="FOZZIL Icon" className="w-full h-full object-cover" />
            </div>
            <div 
              className="flex items-center gap-3 md:gap-2 cursor-pointer md:cursor-default"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <h1 className={`text-[15px] md:text-[19px] font-semibold whitespace-nowrap ${isDark ? 'text-white' : 'text-[#1d1d1f]'}`}>FOZZIL Account</h1>
              {isMobileMenuOpen ? (
                <ChevronUp 
                  size={20} 
                  className={`md:hidden ${isDark ? 'text-gray-400' : 'text-gray-500'}`} 
                />
              ) : (
                <ChevronDown 
                  size={20} 
                  className={`md:hidden ${isDark ? 'text-gray-400' : 'text-gray-500'}`} 
                />
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 md:gap-6">
            <a 
              href={process.env.REACT_APP_MAIN_URL || "https://fozzil.in"} 
              className={`text-[11px] md:text-[13px] font-medium whitespace-nowrap ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
            >
              Back to Home
            </a>
            <button 
              onClick={async () => {
                await cleanupDeviceSession();
                logout();
              }}
              className="bg-[#065DC0] text-white px-2.5 py-1 md:px-3 md:py-1 rounded-full text-[10px] md:text-[11px] font-bold hover:bg-blue-600 transition-all flex items-center justify-center"
            >
              Sign out
            </button>
          </div>
        </div>

        {/* Mobile Navigation Dropdown - Attached to topbar */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsMobileMenuOpen(false)}
                className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[-1] md:hidden"
              />
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ type: "spring", stiffness: 350, damping: 35 }}
                className={`md:hidden absolute top-full left-0 right-0 ${isDark ? 'bg-black/95 border-white/5 shadow-[0_20px_50px_rgba(0,0,0,0.5)]' : 'bg-white border-gray-100 shadow-[0_20px_50px_rgba(0,0,0,0.1)]'} backdrop-blur-3xl border-b rounded-b-[2.5rem] z-[100] overflow-hidden`}
              >
                <div className="px-5 py-2 flex flex-col gap-0.5 pb-6">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => {
                          const tabMatch = tabs.find(t => t.id === tab.id);
                          if (tabMatch) navigate(tabMatch.path);
                          setIsMobileMenuOpen(false);
                        }}
                        className={`flex items-center gap-5 w-full px-6 py-3 transition-colors ${
                          isActive 
                            ? (isDark ? 'text-blue-400 font-bold' : 'text-blue-600 font-bold') 
                            : (isDark ? 'text-gray-400 hover:text-white' : 'text-gray-700 hover:text-black')
                        }`}
                      >
                        <Icon size={22} className={isActive ? (isDark ? 'text-blue-400' : 'text-blue-500') : (isDark ? 'text-gray-500' : 'text-gray-400')} />
                        <span className="text-[17px] tracking-tight">{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>

      <div className="max-w-[1200px] mx-auto pt-6 md:pt-8 px-4 md:px-12 pb-4">
        <div className="flex flex-col md:flex-row gap-8 md:gap-16">
          
          {/* Sidebar */}
          <div className="w-full md:w-[260px] flex-shrink-0">
            <div className="text-center mb-6 md:mb-10 flex flex-col items-center">
              <div 
                className={`w-[100px] h-[100px] md:w-[120px] md:h-[120px] rounded-full mb-4 md:mb-6 shadow-[0_0_30px_rgba(59,130,246,0.15)] ring-[1.5px] ${isDark ? 'ring-blue-500/40 bg-blue-500/5' : 'ring-blue-500/20 bg-blue-50'} backdrop-blur-md cursor-pointer group relative overflow-hidden flex items-center justify-center transition-all duration-500 hover:ring-blue-500/60 hover:shadow-[0_0_40px_rgba(59,130,246,0.3)]`}
                onClick={() => setShowPhotoModal(true)}
              >
                {isProfileLoading ? (
                  <div className={`w-full h-full animate-pulse rounded-full ${isDark ? 'bg-white/5' : 'bg-gray-100'}`} />
                ) : profile?.avatar_url ? (
                  <img 
                    src={profile.avatar_url} 
                    alt="Avatar" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className={`text-[42px] font-bold ${isDark ? 'text-blue-400/40' : 'text-blue-300'} tracking-tighter`}>{getInitials()}</span>
                )}
                
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center transition-opacity">
                  <Camera className="text-white mb-1" size={24} />
                  <span className="text-white text-[10px] font-bold uppercase tracking-wider">Edit</span>
                </div>
              </div>
                <h2 className={`text-[20px] font-bold ${isDark ? 'text-white' : 'text-[#1d1d1f]'} truncate w-full px-4`}>
                  {profile?.first_name ? `${profile.first_name} ${profile.last_name}` : (user?.user_metadata?.full_name || user?.user_metadata?.first_name || user?.email?.split('@')[0])}
                </h2>
              <p className={`text-[13px] ${isDark ? 'text-gray-400' : 'text-[#86868b]'} mt-1`}>{user.email}</p>
            </div>

            <nav className="space-y-1 hidden md:block">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      const tabMatch = tabs.find(t => t.id === tab.id);
                      if (tabMatch) navigate(tabMatch.path);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-[14px] font-medium transition-colors ${
                      activeTab === tab.id 
                      ? (isDark ? "text-blue-400" : "text-blue-500")
                      : (isDark ? "text-gray-400 hover:text-white" : "text-[#1d1d1f] hover:text-black")
                    }`}
                  >
                    <Icon size={18} className={activeTab === tab.id ? (isDark ? "text-blue-400" : "text-blue-500") : (isDark ? "text-gray-500" : "text-[#1d1d1f] opacity-60")} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>

              {/* Mobile Menu Removed from here to be placed at root level */}
          </div>

          {/* Main Content Area */}
          <div className="flex-grow max-w-[800px]">
            <AnimatePresence mode="wait">
              {renderContent()}
            </AnimatePresence>
          </div>

        </div>
      </div>

      {/* Profile Photo Modal */}
      <AnimatePresence>
        {showPhotoModal && (
          <div className="fixed inset-0 z-[2000] flex items-center justify-center p-6 bg-black/60 backdrop-blur-md" onClick={() => setShowPhotoModal(false)}>
              <motion.div 
                initial={{ opacity: 0, scale: 0.92, y: 60 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.92, y: 60 }}
                transition={{ 
                  duration: 0.6,
                  ease: [0.16, 1, 0.3, 1] 
                }}
                onClick={(e) => e.stopPropagation()}
                className={`relative w-full max-w-[820px] rounded-[32px] overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.5)] flex flex-col border backdrop-blur-3xl ${
                  isDark
                    ? 'bg-black border-blue-500/25 text-white'
                    : 'bg-white border-blue-200 text-black'
                }`}
                style={{ height: '480px' }}
              >
              {/* Blue Glows */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/15 blur-[100px] pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 blur-[80px] pointer-events-none" />
              {/* Header */}
              <div className={`flex items-center justify-between px-8 py-5 border-b ${ isDark ? 'border-white/5' : 'border-gray-100' }`}>
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-1 overflow-hidden border ${ isDark ? 'bg-black border-white/10' : 'bg-gray-100 border-gray-200' }`}>
                    <img 
                      src="/logoicon.png" 
                      alt="FOZZIL" 
                      className={editingItem === "new_payment" ? "h-5 w-5 invert" : "w-full h-full object-cover"}
                    />
                  </div>
                  <span className={`text-xl font-bold tracking-tighter ${ isDark ? 'text-white' : 'text-black' }`}>Account</span>
                </div>
                <button
                  onClick={() => {
                    setShowPhotoModal(false);
                    setSelectedImage(null);
                    setZoom(1);
                    setRotation(0);
                    setShowCancelConfirm(false);
                  }}
                  className={`px-5 py-1.5 rounded-[32px] text-sm font-semibold transition-all border ${
                    isDark
                      ? 'bg-white/5 text-white hover:bg-white/10 border-white/10'
                      : 'bg-gray-100 text-black hover:bg-gray-200 border-gray-200'
                  }`}
                >
                  Close
                </button>
              </div>

              <div className="flex-1 flex flex-col items-center justify-center px-8 py-4 gap-5">
                <div className="text-center">
                  <h3 className="text-[20px] font-bold tracking-tight">Profile Photo</h3>
                  <p className="text-[12px] text-gray-500 mt-0.5">A photo helps people recognize you.</p>
                </div>

                <div className="flex justify-center">
                  <div className={`relative w-[160px] h-[160px] flex-shrink-0 rounded-full overflow-hidden shadow-[0_0_40px_rgba(59,130,246,0.1)] ring-[1.5px] ${isDark ? 'bg-blue-500/5 ring-blue-500/40' : 'bg-blue-50 ring-blue-500/20 shadow-inner'} backdrop-blur-md flex items-center justify-center`}>
                    {selectedImage ? (
                      <motion.img 
                        src={selectedImage} 
                        style={{ scale: zoom, rotate: rotation }}
                        className="w-full h-full object-cover"
                      />
                    ) : profile?.avatar_url ? (
                      <img src={profile.avatar_url} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <span className={`text-[56px] font-bold ${isDark ? 'text-blue-400/30' : 'text-blue-200'}`}>{getInitials()}</span>
                    )}
                    
                    {!selectedImage && (
                      <label className="absolute inset-0 cursor-pointer flex items-center justify-center bg-black/5 hover:bg-black/10 transition-colors">
                        <input 
                          type="file" 
                          className="hidden" 
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              const reader = new FileReader();
                              reader.onload = () => setSelectedImage(reader.result);
                              reader.readAsDataURL(file);
                            }
                          }}
                        />
                      </label>
                    )}
                  </div>
                </div>

                {selectedImage && (
                  <div className="w-full max-w-[320px] space-y-3">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setZoom(z => Math.max(1, parseFloat((z - 0.1).toFixed(2))))}
                        className="p-1 rounded-full hover:bg-white/10 transition-all flex-shrink-0"
                      >
                        <Minus size={16} className="text-gray-400" />
                      </button>
                      <input 
                        type="range" 
                        min="1" max="3" step="0.01" 
                        value={zoom} 
                        onChange={(e) => setZoom(parseFloat(e.target.value))}
                        className={`flex-grow accent-blue-500 h-1 rounded-lg appearance-none ${ isDark ? 'bg-white/10' : 'bg-gray-200' }`} 
                      />
                      <button
                        onClick={() => setZoom(z => Math.min(3, parseFloat((z + 0.1).toFixed(2))))}
                        className="p-1 rounded-full hover:bg-white/10 transition-all flex-shrink-0"
                      >
                        <Plus size={16} className="text-gray-400" />
                      </button>
                    </div>
                    <div className="flex justify-center gap-4">
                      <button onClick={() => setRotation(r => r - 90)} className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-all">
                        <RotateCw size={18} className="scale-x-[-1]" />
                      </button>
                      <button onClick={() => setRotation(r => r + 90)} className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-all">
                        <RotateCw size={18} />
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex flex-col items-center gap-3">
                  {selectedImage ? (
                    <>
                      {/* Centered overlay confirmation */}
                      {showCancelConfirm && (
                        <div className="absolute inset-0 z-50 flex items-center justify-center rounded-[32px] backdrop-blur-sm bg-black/40">
                          <div className={`flex flex-col items-center gap-4 px-8 py-6 rounded-[32px] border shadow-2xl ${ isDark ? 'bg-black border-white/10' : 'bg-white border-gray-200' }`}>
                            <p className={`text-sm font-bold ${ isDark ? 'text-white' : 'text-black' }`}>Discard this photo?</p>
                            <p className="text-xs text-gray-500 text-center -mt-2">Your changes will be lost.</p>
                            <div className="flex gap-3">
                              <button
                                onClick={() => {
                                  setSelectedImage(null);
                                  setZoom(1);
                                  setRotation(0);
                                  setShowCancelConfirm(false);
                                }}
                                className="px-5 py-2 rounded-full text-xs font-bold bg-red-500 text-white hover:bg-red-400 transition-all shadow-lg"
                              >
                                Yes, discard
                              </button>
                              <button
                                onClick={() => setShowCancelConfirm(false)}
                                className={`px-5 py-2 rounded-full text-xs font-bold border transition-all ${ isDark ? 'bg-white/5 border-white/10 text-white hover:bg-white/10' : 'bg-gray-100 border-gray-200 text-black hover:bg-gray-200' }`}
                              >
                                Keep editing
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                          <button 
                            disabled={isSaving}
                            onClick={async () => {
                              setIsSaving(true);
                              const { error } = await supabase.from('profiles').update({ avatar_url: selectedImage }).eq('id', user.id);
                              if (!error) {
                                setProfile({...profile, avatar_url: selectedImage});
                                setShowPhotoModal(false);
                                setSelectedImage(null);
                              }
                              setIsSaving(false);
                            }}
                            className={`w-[140px] py-2.5 rounded-full font-black uppercase tracking-widest transition-all text-xs shadow-[0_4px_20px_rgba(59,130,246,0.4)] ${
                              isSaving
                                ? 'bg-gray-400 cursor-not-allowed text-white'
                                : 'bg-blue-600 text-white hover:bg-blue-500 hover:scale-105 active:scale-95'
                            }`}
                          >
                            {isSaving ? "Saving..." : "Save Photo"}
                          </button>
                          <button
                            onClick={() => setShowCancelConfirm(true)}
                            className={`px-4 py-2.5 rounded-full font-bold text-xs border transition-all ${ isDark ? 'bg-white/5 border-white/10 text-white hover:bg-white/10' : 'bg-gray-100 border-gray-200 text-black hover:bg-gray-200' }`}
                          >
                            Cancel
                          </button>
                        </div>
                    </>
                  ) : (
                    <label className="w-[180px] py-2.5 rounded-full font-black uppercase tracking-widest transition-all text-xs shadow-[0_4px_20px_rgba(59,130,246,0.4)] flex items-center justify-center cursor-pointer bg-blue-600 text-white hover:bg-blue-500 hover:scale-105 active:scale-95">
                      Choose Photo
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = () => setSelectedImage(reader.result);
                            reader.readAsDataURL(file);
                          }
                        }}
                      />
                    </label>
                  )}


                </div>
              </div>
          </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Editing Hub Modal */}
      <AnimatePresence mode="wait">
        {editingItem && (
          <div className="fixed inset-0 z-[2000] flex items-center justify-center p-6 bg-black/60 backdrop-blur-md" onClick={() => setEditingItem(null)}>
            <motion.div 
              layout
              initial={{ opacity: 0, scale: 0.92, y: 60 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 60 }}
              transition={{ 
                duration: 0.6,
                ease: [0.16, 1, 0.3, 1]
              }}
              onClick={(e) => e.stopPropagation()}
              className={`relative w-full max-w-[820px] rounded-[32px] overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.5)] flex flex-col border backdrop-blur-3xl ${
                isDark
                  ? 'bg-black border-blue-500/25 text-white'
                  : 'bg-white border-blue-200 text-black'
              }`}
              style={{ height: '480px' }}
            >
              {/* Blue Glows */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/15 blur-[100px] pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 blur-[80px] pointer-events-none" />
              {/* Header */}
              <div className={`flex items-center justify-between px-8 py-5 border-b ${ isDark ? 'border-white/5' : 'border-gray-100' }`}>
                <div className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-1 overflow-hidden border ${ isDark ? 'bg-black border-white/10' : 'bg-gray-100 border-gray-200' }`}>
                    <img 
                      src="/logoicon.png" 
                      alt="FOZZIL" 
                      className={editingItem === "new_payment" ? "h-5 w-5 invert" : "w-full h-full object-cover"}
                    />
                  </div>
                  <span className={`text-xl font-bold tracking-tighter ${ isDark ? 'text-white' : 'text-black' }`}>
                    {['name', 'dob', 'country', 'language'].includes(editingItem) ? 'Personal Information' :
                     ['email_phone', 'password', 'security', 'recovery', 'legacy', 'signin', 'app_passwords'].includes(editingItem) ? 'Sign-In & Security' :
                     ['payment', 'new_payment', 'billing_address', 'shipping_address', 'upi'].includes(editingItem) ? 'Payment & Billing' :
                     'Account'}
                  </span>
                </div>
                <button
                  onClick={() => setEditingItem(null)}
                  className={`px-5 py-1.5 rounded-[32px] text-sm font-semibold transition-all border ${
                    isDark
                      ? 'bg-white/5 text-white hover:bg-white/10 border-white/10'
                      : 'bg-gray-100 text-black hover:bg-gray-200 border-gray-200'
                  }`}
                >
                  Close
                </button>
              </div>

              <div className="flex-1 overflow-y-auto custom-scrollbar p-8 md:px-10 md:py-6 flex flex-col items-center">
                <div className={`w-14 h-14 bg-blue-500/10 text-blue-400 rounded-full flex items-center justify-center mb-4 ring-1 ring-blue-500/20`}>
                  {editingItem === 'name' && <User size={28} />}
                  {editingItem === 'dob' && <CalendarClock size={28} />}
                  {editingItem === 'country' && <Globe size={28} />}
                  {editingItem === 'language' && <LanguagesIcon size={28} />}
                  {editingItem === 'password' && <PasswordIcon size={28} />}
                  {editingItem === 'security' && <AccountSecurityIcon size={32} />}
                  {editingItem === 'recovery' && <Shield size={32} />}
                  {editingItem === 'legacy' && <Users size={32} />}
                  {editingItem === 'signin' && <Monitor size={32} />}
                  {editingItem === 'app_passwords' && <Key size={32} />}
                  {editingItem === 'email_phone' && <Mail size={32} />}
                  {editingItem === 'upi' && <div className={`w-full flex items-center justify-center font-bold italic tracking-tighter ${isDark ? 'text-white' : 'text-black'}`}>UPI</div>}
                  {editingItem === 'new_payment' && <CreditCard size={28} className="text-blue-400" />}
                  {editingItem === 'shipping_address' && <div className="text-blue-500"><Plus size={28} /></div>}
                  {editingItem === 'device_detail' && (viewingDevice?.type === 'laptop' ? <Laptop size={28} /> : <Smartphone size={28} />)}
                </div>
                
                <h3 className={`text-[24px] font-bold tracking-tight text-center ${editingItem === 'password' ? 'max-w-[300px]' : ''} ${editingItem === 'name' ? 'mb-1' : ''}`}>
                  {editingItem === 'name' ? 'Name' : 
                   editingItem === 'dob' ? 'Date of Birth' : 
                   editingItem === 'country' ? 'Country / Region' : 
                   editingItem === 'language' ? 'Language' :
                   editingItem === 'password' ? 'Password' :
                   editingItem === 'security' ? 'Account Security' :
                   editingItem === 'recovery' ? 'Account Recovery' :
                   editingItem === 'legacy' ? 'Legacy Contact' :
                   editingItem === 'signin' ? 'Sign In with FOZZIL' :
                   editingItem === 'app_passwords' ? 'App-Specific Passwords' :
                   editingItem === 'upi' ? 'UPI' :
                   editingItem === 'new_payment' ? 'New Payment Method' :
                   editingItem === 'billing_address' ? 'Billing Address' :
                   editingItem === 'device_detail' ? viewingDevice?.name :
                   'Email & Phone Numbers'}
                </h3>

                {editingItem === 'device_detail' && (
                  <div className="text-center mt-1">
                    <p className="text-[17px] font-bold text-gray-800">{viewingDevice?.model}</p>
                    <p className="text-[14px] text-gray-500">Serial number: {viewingDevice?.serial}</p>
                  </div>
                )}

                {editingItem === 'new_payment' && (
                  <div className="text-center mt-2">
                    <p className={`text-[13px] font-bold mb-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>This will be saved as your new default payment method.</p>
                    <p className={`text-xs leading-relaxed max-w-[400px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>You will not be charged until you make a purchase. You authorise FOZZIL to charge this default payment method for paid transactions, and, if there is a problem, your next eligible payment methods. If you are a Family Organiser with Purchase Sharing enabled, your eligible payment methods may be shared for family member purchases.</p>
                    <p className={`text-xs mt-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Your payment method may be used to confirm your age and eligibility for FOZZIL Services.</p>
                  </div>
                )}

                {editingItem === 'shipping_address' && (
                  <p className="text-[13px] text-gray-500 mt-2 text-center">This shipping address will be used when you make FOZZIL Store purchases.</p>
                )}

                {editingItem === 'password' && (
                  <div className="text-center mt-4 max-w-[380px]">
                    {passwordResetSent ? (
                      <div className={`p-4 rounded-[32px] ${isDark ? 'bg-green-500/10 border border-green-500/20' : 'bg-green-50 border border-green-200'}`}>
                        <CheckCircle size={24} className="text-green-500 mx-auto mb-2" />
                        <p className="text-green-500 font-bold text-[14px]">Reset email sent!</p>
                        <p className={`text-[13px] mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Check your inbox at <strong>{user.email}</strong> for a password reset link. The link will expire in 24 hours.</p>
                      </div>
                    ) : (
                      <>
                        <p className={`text-[14px] leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          We'll send a secure password reset link to your email address. Click the link to set a new password.
                        </p>
                        {passwordResetError && <p className="text-red-500 text-[13px] mt-2">{passwordResetError}</p>}
                        <p className={`text-[12px] mt-3 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Reset link will be sent to: <span className="font-bold">{user.email}</span></p>
                      </>
                    )}
                  </div>
                )}
                
                {editingItem === 'security' && (
                  <div className="text-center mt-2">
                    <p className="font-bold text-[15px]">Two-factor authentication</p>
                    <div className="flex items-center justify-center gap-2 mt-2">
                      <div className={`w-2 h-2 rounded-full ${profile?.phone ? 'bg-green-500' : 'bg-gray-500'}`} />
                      <p className="text-[13px] text-gray-500">{profile?.phone ? `1 trusted phone number (${formatPhoneDisplay(profile.phone, profile.country_region)})` : 'No trusted phone number'}</p>
                    </div>
                    <div className="flex items-center justify-center gap-2 mt-1">
                      <div className={`w-2 h-2 rounded-full ${devices.length > 0 ? 'bg-green-500' : 'bg-gray-500'}`} />
                      <p className="text-[13px] text-gray-500">{devices.length > 0 ? `${devices.length} trusted device${devices.length > 1 ? 's' : ''}` : 'No trusted devices'}</p>
                    </div>
                    <p className={`text-[14px] mt-4 max-w-[360px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>When you sign in on a new device or web browser, a verification code will be sent to your FOZZIL devices or one of your trusted phone numbers.</p>
                    {securitySaveMsg && (
                      <div className={`mt-3 p-2 rounded-xl ${isDark ? 'bg-green-500/10' : 'bg-green-50'}`}>
                        <p className="text-green-500 text-[13px] font-medium flex items-center justify-center gap-1.5"><CheckCircle size={14} />{securitySaveMsg}</p>
                      </div>
                    )}
                  </div>
                )}

                {editingItem === 'recovery' && (
                  <div className="mt-4 text-center max-w-[380px]">
                    <p className={`text-[14px] leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      If you lose access to your account, a recovery method can help you get your account and data back. Your device passcodes can be used to recover end-to-end encrypted data.
                    </p>
                    {recoveryError && <p className="text-red-500 text-[13px] mt-2">{recoveryError}</p>}
                    {recoverySaveMsg && (
                      <div className={`mt-3 p-2 rounded-xl ${isDark ? 'bg-green-500/10' : 'bg-green-50'}`}>
                        <p className="text-green-500 text-[13px] font-medium flex items-center justify-center gap-1.5"><CheckCircle size={14} />{recoverySaveMsg}</p>
                      </div>
                    )}
                  </div>
                )}

                {editingItem === 'legacy' && (
                  <div className="mt-4 text-center max-w-[380px]">
                    <p className={`text-[14px] leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Legacy contacts will be able to access the data stored in your account after your death, including photos, messages, files, and more.
                    </p>
                    {legacyError && <p className="text-red-500 text-[13px] mt-2">{legacyError}</p>}
                    {legacySaveMsg && (
                      <div className={`mt-3 p-2 rounded-xl ${isDark ? 'bg-green-500/10' : 'bg-green-50'}`}>
                        <p className="text-green-500 text-[13px] font-medium flex items-center justify-center gap-1.5"><CheckCircle size={14} />{legacySaveMsg}</p>
                      </div>
                    )}
                  </div>
                )}

                {editingItem === 'signin' && (
                  <div className="text-center">
                    <p className="text-[15px] font-bold mt-1">{connectedApps.length} apps and websites</p>
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-500 text-[14px] mt-2 inline-block hover:underline">
                      About Sign in with FOZZIL & Privacy ↗
                    </a>
                  </div>
                )}

                {editingItem === 'app_passwords' && (
                  <div className="text-center">
                    <p className="text-[15px] font-bold mt-1">No passwords</p>
                    <p className="text-[14px] text-gray-500 mt-4 max-w-[360px]">Use an app-specific password to sign in to an app by FOZZIL.</p>
                    <button className="text-blue-500 text-[14px] mt-2 hover:underline">About app-specific passwords ↗</button>
                  </div>
                )}

                <div className={`space-y-6 px-1 w-full ${editingItem === 'name' ? 'mt-1' : 'mt-4'}`}>
                {editingItem === 'signin' && (
                  <div className="space-y-3">
                    {connectedApps.length > 0 ? (
                      connectedApps.map((app, i) => {
                        const APP_LOGOS = {
                          "T.A.E.L.S": "/TAELS Logo black bg.png",
                          "MEDOS": "/FOZZIL MEDOS LOGO.png",
                          "ZYPHER AI": "/ZYPHER logo wobg.png",
                          "ARK": "/logoicon.png"
                        };
                        const iconPath = APP_LOGOS[app.name] || "/logoicon.png";
                        
                        return (
                          <div key={i} className={`relative overflow-hidden flex items-center justify-between p-4 rounded-[32px] border backdrop-blur-[40px] transition-all duration-700 ${
                            isDark 
                              ? 'border-white/10 bg-white/[0.03] shadow-2xl shadow-blue-950/20 hover:border-blue-500/30' 
                              : 'border-blue-100 bg-white/70 shadow-xl shadow-blue-500/5 hover:border-blue-200'
                          } group cursor-pointer`}>
                            {/* Blue Background Shade */}
                            <div className={`absolute top-0 right-0 w-24 h-24 blur-[40px] pointer-events-none transition-opacity duration-700 ${
                              isDark ? 'bg-blue-500/15 opacity-100' : 'bg-blue-600/5 opacity-50 group-hover:opacity-100'
                            }`} />
                            <div className={`absolute bottom-0 left-0 w-16 h-16 blur-[25px] pointer-events-none transition-opacity duration-700 ${
                              isDark ? 'bg-blue-600/10 opacity-100' : 'bg-[#065DC0]/5 opacity-50 group-hover:opacity-100'
                            }`} />
                            <div className="flex items-center gap-4">
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center overflow-hidden ${isDark ? 'bg-white/10' : 'bg-gray-50'}`}>
                                <img src={iconPath} alt={app.name} className="w-full h-full object-cover" />
                              </div>
                              <span className="font-bold">{app.name}</span>
                            </div>
                            <ChevronRight size={16} className="text-gray-300 group-hover:text-blue-500 transition-colors" />
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        No connected apps found.
                      </div>
                    )}
                  </div>
                )}

                {editingItem === 'app_passwords' && (
                  <div className="flex justify-center pt-4">
                    <button className="px-6 py-2.5 underline underline-blue-500 text-blue-500 font-bold hover:bg-blue-500/5 transition-all text-[14px]">
                      Generate an app-specific password
                    </button>
                  </div>
                )}

                {editingItem === 'legacy' && (
                  <div className="space-y-5 w-full">
                    <div className={`p-4 rounded-[32px] border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                      <h4 className="text-[13px] font-bold uppercase tracking-wider text-gray-500 mb-3">Legacy Contact</h4>
                      {editData.legacy_contact && editData._legacyContactSaved ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Users size={16} className="text-green-500" />
                            <div>
                              <span className={`text-[14px] font-medium ${isDark ? 'text-white' : 'text-black'}`}>{editData.legacy_contact}</span>
                              <p className="text-[11px] text-green-500 font-medium">Designated legacy contact</p>
                            </div>
                          </div>
                          <button
                            onClick={() => { setEditData({...editData, legacy_contact: '', _legacyContactSaved: false}); setLegacyError(''); }}
                            className="text-red-500 text-[12px] font-bold hover:underline"
                          >Remove</button>
                        </div>
                      ) : (
                        <div className={`border ${isDark ? 'border-white/10' : 'border-gray-200'} rounded-xl p-2.5 focus-within:ring-2 ring-blue-500/50 transition-all`}>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-0.5 px-2">Contact Email</label>
                          <input
                            type="email"
                            className={`w-full bg-transparent outline-none font-medium px-2 text-[14px] ${isDark ? 'text-white' : 'text-black'}`}
                            placeholder="Enter legacy contact's email"
                            value={editData.legacy_contact || ''}
                            onChange={(e) => { setEditData({...editData, legacy_contact: e.target.value}); setLegacyError(''); }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Info about legacy contact */}
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-blue-500/5 border border-blue-500/10' : 'bg-blue-50 border border-blue-100'}`}>
                      <p className={`text-[12px] leading-relaxed ${isDark ? 'text-blue-300/80' : 'text-blue-600'}`}>
                        <strong>Important:</strong> Your legacy contact will only be granted access after your death has been verified. They will be able to access your photos, messages, notes, files, and other stored data.
                      </p>
                    </div>
                  </div>
                )}


                {editingItem === 'upi' && (
                  <div className="space-y-4 pt-2">
                    <div className={`border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'} rounded-[32px] p-3 focus-within:ring-2 ring-blue-500/50 transition-all`}>
                      <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1 px-4">UPI ID</label>
                      <input 
                        className="w-full bg-transparent outline-none font-medium px-4 text-[16px]" 
                        value={editData.upi_id || ""} 
                        onChange={(e) => setEditData({...editData, upi_id: e.target.value})}
                      />
                    </div>
                    
                    <div className="pt-2">
                      <h4 className="text-[13px] font-bold text-gray-400 uppercase tracking-widest mb-4">Billing Address</h4>
                      <div className="grid grid-cols-2 gap-3">
                        <div className={`border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'} rounded-[32px] p-3`}>
                          <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1 px-4">First Name</label>
                          <input className="w-full bg-transparent outline-none font-medium px-4" value={editData.first_name || ""} onChange={(e) => setEditData({...editData, first_name: e.target.value})} />
                        </div>
                        <div className={`border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'} rounded-[32px] p-3`}>
                          <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1 px-4">Last Name</label>
                          <input className="w-full bg-transparent outline-none font-medium px-4" value={editData.last_name || ""} onChange={(e) => setEditData({...editData, last_name: e.target.value})} />
                        </div>
                      </div>
                      <div className="space-y-3 mt-3">
                        {[1].map(i => (
                          <div key={i} className={`border ${isDark ? 'border-white/10' : 'border-gray-200'} rounded-[32px] p-3`}>
                            <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1 px-4">Street</label>
                            <input className="w-full bg-transparent outline-none font-medium px-4" placeholder="One Infinite Loop" />
                          </div>
                        ))}
                        <div className={`border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'} rounded-[32px] p-3`}>
                          <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1 px-4">City/Town</label>
                          <input className="w-full bg-transparent outline-none font-medium px-4" value={editData.city || ""} onChange={(e) => setEditData({...editData, city: e.target.value})} />
                        </div>
                        <div className={`border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'} rounded-[32px] p-3`}>
                          <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1 px-4">State</label>
                          <select className="w-full bg-transparent outline-none font-medium px-4" value={editData.state || ""} onChange={(e) => setEditData({...editData, state: e.target.value})}>
                            <option value="California">California</option>
                            <option value="New York">New York</option>
                            <option value="Texas">Texas</option>
                          </select>
                        </div>
                        <div className={`border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'} rounded-[32px] p-3`}>
                          <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1 px-4">Postcode</label>
                          <input className="w-full bg-transparent outline-none font-medium px-4" value={editData.pincode || ""} onChange={(e) => setEditData({...editData, pincode: e.target.value})} />
                        </div>
                      </div>
                      <button className="text-blue-500 font-bold border border-blue-500 rounded-xl px-4 py-2 mt-6 text-[13px] hover:bg-blue-500/5 transition-all">
                        Remove UPI
                      </button>
                    </div>
                  </div>
                )}

                {editingItem === 'new_payment' && (
                  <div className="w-full flex flex-col pt-2 overflow-y-auto max-h-[300px] custom-scrollbar">
                    {/* Row 1: Payment Type */}
                    <div className={`flex flex-col md:flex-row items-start px-2 py-5 border-b ${isDark ? 'border-white/5' : 'border-black/5'}`}>
                      <div className={`w-full md:w-[140px] shrink-0 mb-3 md:mb-0 md:pt-1 text-[10px] font-extrabold uppercase tracking-widest ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                        Payment Method
                      </div>
                      <div className="flex-grow w-full">
                        <div className={`border-2 ${isDark ? 'border-blue-500/20 bg-blue-500/5' : 'border-blue-100 bg-blue-50/30'} rounded-[32px] px-4 py-2 shadow-sm transition-all focus-within:ring-2 ring-blue-500/30`}>
                          <select 
                            className={`w-full bg-transparent outline-none font-bold text-[15px] cursor-pointer ${isDark ? 'text-white' : 'text-black'}`}
                            value={editData.payment_type || 'UPI'}
                            onChange={(e) => setEditData({...editData, payment_type: e.target.value})}
                          >
                            <option value="UPI" className={isDark ? 'bg-[#1c1c1e]' : 'bg-white'}>UPI</option>
                            <option value="Card" className={isDark ? 'bg-[#1c1c1e]' : 'bg-white'}>Credit or Debit Card</option>
                            <option value="Net Banking" className={isDark ? 'bg-[#1c1c1e]' : 'bg-white'}>Net Banking</option>
                            <option value="Wallet" className={isDark ? 'bg-[#1c1c1e]' : 'bg-white'}>Digital Wallet</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Row 2: Payment Details */}
                    <div className={`flex flex-col md:flex-row items-start px-2 py-5 border-b ${isDark ? 'border-white/5' : 'border-black/5'}`}>
                      <div className={`w-full md:w-[140px] shrink-0 mb-3 md:mb-0 md:pt-1 text-[10px] font-extrabold uppercase tracking-widest ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                        Details
                      </div>
                      <div className="flex-grow w-full">
                        {editData.payment_type === 'Card' ? (
                          <div className={`border-2 ${isDark ? 'border-blue-500/30 bg-blue-500/10' : 'border-blue-100 bg-blue-50'} rounded-[32px] p-4 text-center backdrop-blur-md`}>
                            <p className={`text-[12px] font-bold mb-1 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Verification Required</p>
                            <p className={`text-[11px] opacity-70 ${isDark ? 'text-blue-100' : 'text-blue-800'}`}>
                              A refundable amount of ₹2.00 will be deducted to verify your card details.
                            </p>
                          </div>
                        ) : (
                          <div className={`border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'} rounded-[32px] p-4 focus-within:ring-2 ring-blue-500/40 transition-all shadow-sm`}>
                            <input 
                              className={`w-full bg-transparent outline-none font-bold text-[15px] ${isDark ? 'text-white' : 'text-black'}`}
                              placeholder={editData.payment_type === 'Wallet' ? 'e.g. Amazon Pay, Paytm' : 
                                           editData.payment_type === 'Net Banking' ? 'Enter Bank Name' : 'yourname@upi'} 
                              value={editData.upi_id || ""}
                              onChange={(e) => setEditData({...editData, upi_id: e.target.value})}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Row 3: Billing Address Header */}
                    <div className="px-2 py-4">
                       <h4 className="text-[10px] font-black text-blue-500 uppercase tracking-[0.3em] text-center opacity-60">Billing Address</h4>
                    </div>

                    {/* Row 4: Name */}
                    <div className={`flex flex-col md:flex-row items-start px-2 py-3 border-b ${isDark ? 'border-white/5' : 'border-black/5'}`}>
                      <div className={`w-full md:w-[140px] shrink-0 mb-2 md:mb-0 md:pt-1 text-[10px] font-extrabold uppercase tracking-widest ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
                        Full Name
                      </div>
                      <div className="flex-grow w-full flex gap-3">
                        <div className={`flex-1 border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'} rounded-[32px] p-3`}>
                          <input className="w-full bg-transparent outline-none font-medium px-2 text-[14px]" placeholder="First" value={editData.first_name || ""} onChange={(e) => setEditData({...editData, first_name: e.target.value})} />
                        </div>
                        <div className={`flex-1 border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'} rounded-[32px] p-3`}>
                          <input className="w-full bg-transparent outline-none font-medium px-2 text-[14px]" placeholder="Last" value={editData.last_name || ""} onChange={(e) => setEditData({...editData, last_name: e.target.value})} />
                        </div>
                      </div>
                    </div>

                    {/* Row 5: Address */}
                    <div className={`flex flex-col md:flex-row items-start px-2 py-4`}>
                      <div className={`w-full md:w-[140px] shrink-0 mb-2 md:mb-0 md:pt-1 text-[10px] font-extrabold uppercase tracking-widest ${isDark ? 'text-zinc-600' : 'text-zinc-400'}`}>
                        Address
                      </div>
                      <div className={`flex-grow w-full border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'} rounded-[32px] p-3`}>
                        <input 
                          className="w-full bg-transparent outline-none font-medium px-2 text-[14px]" 
                          placeholder="Street, locality, area"
                          value={editData.address_line || ""} 
                          onChange={(e) => setEditData({...editData, address_line: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {editingItem === 'billing_address' && (
                  <div className="space-y-4 pt-2">
                    <div className="grid grid-cols-2 gap-3">
                      <div className={`border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'} rounded-[32px] p-3 focus-within:ring-2 ring-blue-500/50 transition-all`}>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1 px-4">First Name</label>
                        <input 
                          className="w-full bg-transparent outline-none font-medium px-4" 
                          value={editData.first_name || ""} 
                          onChange={(e) => setEditData({...editData, first_name: e.target.value})}
                        />
                      </div>
                      <div className={`border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'} rounded-[32px] p-3 focus-within:ring-2 ring-blue-500/50 transition-all`}>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1 px-4">Last Name</label>
                        <input 
                          className="w-full bg-transparent outline-none font-medium px-4" 
                          value={editData.last_name || ""} 
                          onChange={(e) => setEditData({...editData, last_name: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className={`border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'} rounded-[32px] p-3 focus-within:ring-2 ring-blue-500/50 transition-all`}>
                      <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1 px-4">Street / Address Line</label>
                      <input 
                        className="w-full bg-transparent outline-none font-medium px-4" 
                        value={editData.address_line || ""} 
                        placeholder="Enter street address"
                        onChange={(e) => setEditData({...editData, address_line: e.target.value})}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className={`border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'} rounded-[32px] p-3 focus-within:ring-2 ring-blue-500/50 transition-all`}>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1 px-4">City / Town</label>
                        <input 
                          className="w-full bg-transparent outline-none font-medium px-4" 
                          value={editData.city || ""} 
                          onChange={(e) => setEditData({...editData, city: e.target.value})}
                        />
                      </div>
                      <div className={`border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'} rounded-[32px] p-3 focus-within:ring-2 ring-blue-500/50 transition-all`}>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1 px-4">State / Province</label>
                        <input 
                          className="w-full bg-transparent outline-none font-medium px-4" 
                          value={editData.state || ""} 
                          onChange={(e) => setEditData({...editData, state: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className={`border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'} rounded-[32px] p-3 focus-within:ring-2 ring-blue-500/50 transition-all`}>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1 px-4">Postcode / Zip</label>
                        <input 
                          className="w-full bg-transparent outline-none font-medium px-4" 
                          value={editData.pincode || ""} 
                          onChange={(e) => setEditData({...editData, pincode: e.target.value})}
                        />
                      </div>
                      <div className={`border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'} rounded-[32px] p-3 focus-within:ring-2 ring-blue-500/50 transition-all`}>
                        <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1 px-4">Country</label>
                        <select 
                          className="w-full bg-transparent outline-none font-medium px-4"
                          value={editData.country || ""}
                          onChange={(e) => setEditData({...editData, country: e.target.value})}
                        >
                          <option value="">Select Country</option>
                          <option value="United States">United States</option>
                          <option value="United Kingdom">United Kingdom</option>
                          <option value="India">India</option>
                          <option value="Canada">Canada</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {editingItem === 'device_detail' && (
                  <div className="space-y-8 pt-4">
                    <div>
                      <h4 className="text-[14px] font-bold mb-4">Security</h4>
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Minus size={18} className="text-gray-400" />
                            <span className="text-[15px] font-medium">Find My Mac Off</span>
                          </div>
                          <span className="text-gray-400 text-sm">Helps recover a lost or stolen device</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <CheckCircle size={18} className="text-green-500" />
                            <span className="text-[15px] font-medium">Trusted device</span>
                          </div>
                          <span className="text-gray-400 text-sm text-right leading-tight">Can receive FOZZIL Account<br/>verification codes</span>
                        </div>
                      </div>
                    </div>

                    <div className={`p-6 rounded-[32px] border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                      <h4 className="font-bold text-[15px] mb-2 text-gray-800">How to Change Security Settings</h4>
                      <p className="text-[13px] text-gray-500 leading-relaxed mb-4">You can change security preferences in System Preferences on {viewingDevice?.name}.</p>
                    </div>

                    <div>
                      <h4 className="text-[14px] font-bold mb-4">About</h4>
                      <div className="grid grid-cols-2 gap-y-6">
                        <div>
                          <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Model</p>
                          <p className="font-medium">{viewingDevice?.model}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Version</p>
                          <p className="font-medium">{viewingDevice?.version}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-400 font-bold uppercase tracking-wider mb-1">Serial Number</p>
                          <p className="font-medium">{viewingDevice?.serial}</p>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4">
                      <button className="text-blue-500 font-bold border border-blue-500 rounded-xl px-6 py-2.5 text-[14px] hover:bg-blue-500/5 transition-all">
                        Remove from Account
                      </button>
                    </div>
                  </div>
                )}

                {editingItem === 'password' && !passwordResetSent && (
                  <div className="space-y-3 w-full max-w-[400px] mx-auto">
                    <div className={`p-4 rounded-[32px] border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                      <p className={`text-[13px] font-bold mb-1 ${isDark ? 'text-white' : 'text-black'}`}>Your trusted devices</p>
                      {devices.filter(d => d.is_trusted).length > 0 ? (
                        devices.filter(d => d.is_trusted).slice(0, 3).map((dev, i) => (
                          <div key={dev.id || i} className="flex items-center gap-3 py-2">
                            {dev.device_type === 'laptop' || dev.device_type === 'desktop' ? <Laptop size={18} className="text-blue-400" /> : <Smartphone size={18} className="text-blue-400" />}
                            <span className={`text-[14px] font-medium ${isDark ? 'text-white' : 'text-black'}`}>{dev.device_name || 'Unknown Device'}</span>
                          </div>
                        ))
                      ) : (
                        <p className="text-[13px] text-gray-500 mt-1">No trusted devices registered.</p>
                      )}
                    </div>
                  </div>
                )}

                {editingItem === 'security' && (
                  <div className="space-y-6 w-full">
                    {/* Trusted Phone */}
                    <div className={`p-4 rounded-[32px] border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="text-[13px] font-bold uppercase tracking-wider text-gray-500">Trusted Phone</h4>
                        <button
                          onClick={() => {
                            setEditingItem('email_phone');
                            const parts = parsePhoneParts(profile?.phone, profile?.country_region || "India");
                            setEditData({ 
                              email: user.email, 
                              phone: profile?.phone, 
                              showPhoneInput: !profile?.phone || profile?.phone?.startsWith('0'),
                              isPhoneLocked: !!profile?.phone && !profile?.phone?.startsWith('0'),
                              phoneCode: parts.code,
                              phoneNumber: parts.number
                            });
                          }}
                          className="text-blue-500 text-[12px] font-bold hover:underline"
                        >
                          {(profile?.phone && !profile.phone.startsWith('0')) ? 'Change' : '+ Add'}
                        </button>
                      </div>
                      <div className="flex items-center gap-3">
                        <Phone size={16} className="text-blue-500 flex-shrink-0" />
                        <span className={`text-[14px] font-medium ${isDark ? 'text-white' : 'text-black'}`}>
                          {formatPhoneDisplay(profile?.phone, profile?.country_region)}
                        </span>
                      </div>
                    </div>

                    {/* 2FA Toggle */}
                    <div className={`p-4 rounded-[32px] ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50 border border-gray-100'}`}>
                      <div className="flex justify-between items-center">
                        <div>
                          <p className={`text-[14px] font-bold ${isDark ? 'text-white' : 'text-black'}`}>Two-Factor Authentication</p>
                          <p className="text-[12px] text-gray-500 mt-0.5">{editData.two_factor_auth ? 'Extra security is ON' : 'Extra security is OFF'}</p>
                        </div>
                        <Toggle
                          checked={!!editData.two_factor_auth}
                          onChange={(val) => setEditData({...editData, two_factor_auth: val})}
                          isDark={isDark}
                        />
                      </div>
                    </div>

                    {/* Trusted Devices */}
                    <div className={`p-4 rounded-[32px] ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50 border border-gray-100'}`}>
                      <h4 className="text-[13px] font-bold uppercase tracking-wider text-gray-500 mb-3">
                        {devices.filter(d => d.is_trusted).length > 0 ? `${Math.min(devices.filter(d => d.is_trusted).length, 3)} Trusted Device${devices.filter(d => d.is_trusted).length > 1 ? 's' : ''}` : 'No Trusted Devices'}
                      </h4>
                      <div className="space-y-3">
                        {devices.filter(d => d.is_trusted).length > 0 ? (
                          devices.filter(d => d.is_trusted).slice(0, 3).map((dev, i) => (
                            <div key={dev.id || i} className="flex items-center gap-3">
                              {dev.device_type === 'laptop' || dev.device_type === 'desktop' ? <Laptop size={16} className="text-blue-500" /> : <Smartphone size={16} className="text-blue-500" />}
                              <span className={`text-[14px] font-medium ${isDark ? 'text-white' : 'text-black'}`}>{dev.device_name || 'Unknown Device'}</span>
                            </div>
                          ))
                        ) : (
                          <p className="text-[13px] text-gray-500 italic">No trusted devices registered.</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {editingItem === 'recovery' && (
                  <div className="space-y-5 w-full">
                    {/* Recovery Contact */}
                    <div className={`p-4 rounded-[32px] border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                      <h4 className="text-[13px] font-bold uppercase tracking-wider text-gray-500 mb-3">Recovery Contact Email</h4>
                      {editData.recovery_contact && editData._recoveryContactSaved ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Mail size={16} className="text-green-500" />
                            <div>
                              <span className={`text-[14px] font-medium ${isDark ? 'text-white' : 'text-black'}`}>{editData.recovery_contact}</span>
                              <p className="text-[11px] text-green-500 font-medium">Verified recovery contact</p>
                            </div>
                          </div>
                          <button
                            onClick={() => { setEditData({...editData, recovery_contact: '', recovery_setup: false, _recoveryContactSaved: false}); setRecoveryError(''); }}
                            className="text-red-500 text-[12px] font-bold hover:underline"
                          >Remove</button>
                        </div>
                      ) : (
                        <div className={`border ${isDark ? 'border-white/10' : 'border-gray-200'} rounded-xl p-2.5 focus-within:ring-2 ring-blue-500/50 transition-all`}>
                          <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-0.5 px-2">Contact Email</label>
                          <input
                            type="email"
                            className={`w-full bg-transparent outline-none font-medium px-2 text-[14px] ${isDark ? 'text-white' : 'text-black'}`}
                            placeholder="Enter trusted contact's email"
                            value={editData.recovery_contact || ''}
                            onChange={(e) => { setEditData({...editData, recovery_contact: e.target.value}); setRecoveryError(''); }}
                          />
                        </div>
                      )}
                    </div>

                    {/* Recovery Setup status */}
                    <div className={`p-4 rounded-[32px] ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50 border border-gray-100'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-[14px] font-bold ${isDark ? 'text-white' : 'text-black'}`}>Recovery Setup</p>
                          <p className="text-[12px] text-gray-500 mt-0.5">{editData.recovery_setup ? 'Active — recovery contact configured' : 'Not configured'}</p>
                        </div>
                        <Toggle
                          checked={!!editData.recovery_setup}
                          onChange={(val) => setEditData({...editData, recovery_setup: val})}
                          isDark={isDark}
                        />
                      </div>
                    </div>

                    {/* Info about recovery */}
                    <div className={`p-3 rounded-xl ${isDark ? 'bg-blue-500/5 border border-blue-500/10' : 'bg-blue-50 border border-blue-100'}`}>
                      <p className={`text-[12px] leading-relaxed ${isDark ? 'text-blue-300/80' : 'text-blue-600'}`}>
                        <strong>How it works:</strong> If you forget your password or get locked out, FOZZIL will send a recovery code to your trusted contact's email. They can then share the code with you to regain access.
                      </p>
                    </div>
                  </div>
                )}

                {editingItem === 'email_phone' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-[13px] font-bold uppercase tracking-wider text-gray-500">Email Address</h4>
                      </div>
                      <div className="space-y-5">
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-4">
                            <Mail size={18} className="text-blue-500" />
                            <div>
                              <p className="font-medium">{user.email}</p>
                              <p className="text-[12px] text-gray-500">Primary Email</p>
                            </div>
                          </div>
                          <div className="w-5 h-5 rounded-full border-2 border-gray-400 flex items-center justify-center text-gray-400 opacity-20"><Minus size={10} strokeWidth={4} /></div>
                        </div>
                        {profile?.secondary_email && (
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-4">
                              <Mail size={18} className="text-blue-500" />
                              <p className="font-medium">{profile.secondary_email}</p>
                            </div>
                            <div className="w-5 h-5 rounded-full border-2 border-gray-400 flex items-center justify-center text-gray-400"><Minus size={10} strokeWidth={4} /></div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-[13px] font-bold uppercase tracking-wider text-gray-500">Phone Number</h4>
                        {!editData.showPhoneInput && !editData.phone && (
                          <Plus 
                            size={18} 
                            className="text-blue-500 cursor-pointer hover:bg-white/5 rounded-full transition-colors" 
                            onClick={() => setEditData({...editData, showPhoneInput: true})}
                          />
                        )}
                      </div>
                      
                      {(editData.showPhoneInput || editData.phone) ? (
                        <div className={`border ${isDark ? 'border-white/10' : 'border-gray-200'} rounded-[32px] p-2.5 focus-within:ring-2 ring-blue-500/50 transition-all relative group`}>
                          <div className="flex items-center gap-0 px-2 md:px-3 relative z-0">
                            <Phone size={18} className="text-blue-500 flex-shrink-0 mr-2 md:mr-3" />
                            
                            {/* Country Code Picker */}
                            <div className="relative">
                              <button 
                                type="button"
                                onClick={() => {
                                  if (editData.isPhoneLocked) return;
                                  setIsCountryCodeDropdownOpen(!isCountryCodeDropdownOpen);
                                }}

                                className={`flex items-center gap-1 px-2 py-1.5 rounded-xl transition-colors border-r ${isDark ? 'border-white/10' : 'border-gray-100'} mr-1.5 md:mr-3 ${editData.isPhoneLocked ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50/5 cursor-pointer'}`}
                              >
                                <span className="text-[15px]">{countries.find(c => c.code === editData.phoneCode)?.flag || "🇮🇳"}</span>
                                <span className="font-medium text-[15px]">{editData.phoneCode || "+91"}</span>
                                <ChevronDown size={14} className={`opacity-50 transition-transform ${isCountryCodeDropdownOpen ? 'rotate-180' : ''}`} />
                              </button>

                              <AnimatePresence>
                                {isCountryCodeDropdownOpen && (
                                  <motion.div
                                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                    className={`absolute left-0 top-full mt-2 w-64 max-h-80 overflow-hidden rounded-[32px] border shadow-2xl z-[100] flex flex-col backdrop-blur-3xl ${isDark ? 'bg-black/90 border-white/10 text-white' : 'bg-white/95 border-blue-100 text-black'}`}
                                  >
                                    <div className="p-3 border-b border-white/5">
                                      <input 
                                        autoFocus
                                        placeholder="Search country or code..."
                                        className={`w-full ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'} rounded-lg px-3 py-2 text-sm outline-none border focus:border-blue-500/50`}
                                        value={countryCodeSearch}
                                        onChange={(e) => setCountryCodeSearch(e.target.value)}
                                      />
                                    </div>
                                    <div className="overflow-y-auto flex-1 max-h-[240px]">
                                      {countries
                                        .filter(c => c.name.toLowerCase().includes(countryCodeSearch.toLowerCase()) || c.code.includes(countryCodeSearch))
                                        .map(c => (
                                          <div 
                                            key={c.name}
                                            onClick={() => {
                                              setEditData({ ...editData, phoneCode: c.code, country_region: c.name });
                                              setIsCountryCodeDropdownOpen(false);
                                              setCountryCodeSearch('');
                                            }}
                                            className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors ${isDark ? 'hover:bg-white/5' : 'hover:bg-blue-50/50'}`}
                                          >
                                            <div className="flex items-center gap-3">
                                              <span>{c.flag}</span>
                                              <span className="text-sm font-medium">{c.name}</span>
                                            </div>
                                            <span className="text-xs font-bold text-blue-500">{c.code}</span>
                                          </div>
                                        ))
                                      }
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </div>

                            <div className="flex-1 min-w-0">
                              <input 
                                className={`w-full bg-transparent outline-none font-medium text-[15px] ${editData.isPhoneLocked ? 'text-gray-500 cursor-not-allowed' : (isDark ? 'text-white' : 'text-black')}`} 
                                value={editData.phoneNumber || ""} 
                                placeholder="00000 00000"
                                readOnly={editData.isPhoneLocked}
                                onChange={(e) => {
                                  let val = e.target.value.replace(/[^\d]/g, '');
                                  if (val.startsWith('0')) val = val.substring(1);
                                  setEditData({...editData, phoneNumber: val});
                                  if (phoneError) setPhoneError('');
                                }}
                              />
                            </div>
                            <div className="flex items-center gap-1.5 md:gap-3">
                              {profile?.phone && !profile.phone.startsWith('0') && (
                                <button
                                  type="button"
                                  onClick={() => setEditData({...editData, isPhoneLocked: !editData.isPhoneLocked})}
                                  className={`text-[11px] font-bold transition-colors ${editData.isPhoneLocked ? 'text-blue-500 hover:text-blue-400' : 'text-gray-400 hover:text-gray-300'}`}
                                >
                                  {editData.isPhoneLocked ? 'Change' : 'Done'}
                                </button>
                              )}
                              
                              <div className="relative group/tip">
                                <div className="w-5 h-5 rounded-full bg-gray-400 flex items-center justify-center text-white text-[10px] font-bold cursor-help">?</div>
                                <div className="absolute right-0 bottom-full mb-2 w-48 p-2 rounded-xl bg-black/90 text-white text-[10px] font-medium opacity-0 group-hover/tip:opacity-100 transition-opacity pointer-events-none backdrop-blur shadow-xl border border-white/10 z-[101]">
                                   Verification code will be sent to this number
                                </div>
                              </div>
                            </div>
                          </div>
                          {phoneError && <p className="text-red-500 text-[12px] font-bold mt-2 px-2">{phoneError}</p>}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 opacity-60 italic px-4">No phone number registered.</p>
                      )}
                    </div>
                  </div>
                )}

                {editingItem === 'name' && (
                  <div className="space-y-2.5 w-full max-w-[540px] mx-auto">
                    <div className="flex gap-3">
                      <div className={`flex-1 border ${isDark ? 'border-white/10' : 'border-gray-200'} rounded-[32px] p-2.5 focus-within:ring-2 ring-blue-500/50 transition-all`}>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-0 px-3 whitespace-nowrap">First name</label>
                        <input 
                          className="w-full bg-transparent outline-none font-medium px-3 text-[15px]" 
                          value={editData.first_name || ""} 
                          onChange={(e) => setEditData({...editData, first_name: e.target.value})}
                        />
                      </div>
                      <div className={`flex-1 border ${isDark ? 'border-white/10' : 'border-gray-200'} rounded-[32px] p-2.5 focus-within:ring-2 ring-blue-500/50 transition-all`}>
                        <label className="block text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-0 px-3 whitespace-nowrap">Last name</label>
                        <input 
                          className="w-full bg-transparent outline-none font-medium px-3 text-[15px]" 
                          value={editData.last_name || ""} 
                          onChange={(e) => setEditData({...editData, last_name: e.target.value})}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {editingItem === 'dob' && (
                  <div className="w-full flex flex-col items-center">
                    {/* Trigger View */}
                    <div 
                      onClick={() => {
                        setTempDateSelection({
                          day: parseInt(editData.dob_day) || 15,
                          month: editData.dob_month || 'July',
                          year: parseInt(editData.dob_year) || 2005
                        });
                        setIsWheelPickerOpen(true);
                      }}
                      className="flex gap-2 justify-center w-full max-w-[480px] mx-auto cursor-pointer group"
                    >
                      <div className={`w-[80px] border ${isDark ? 'border-white/10' : 'border-gray-200'} rounded-[32px] p-2.5 group-hover:bg-blue-500/5 transition-all text-center`}>
                        <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wider mb-0 w-full">Day</label>
                        <div className="font-medium text-[15px]">{editData.dob_day || "15"}</div>
                      </div>
                      <div className={`w-[140px] border ${isDark ? 'border-white/10' : 'border-gray-200'} rounded-[32px] p-2.5 group-hover:bg-blue-500/5 transition-all text-center`}>
                        <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wider mb-0 w-full">Month</label>
                        <div className="font-medium text-[15px]">{editData.dob_month || "July"}</div>
                      </div>
                      <div className={`w-[100px] border ${isDark ? 'border-white/10' : 'border-gray-200'} rounded-[32px] p-2.5 group-hover:bg-blue-500/5 transition-all text-center`}>
                        <label className="block text-[9px] font-bold text-gray-500 uppercase tracking-wider mb-0 w-full">Year</label>
                        <div className="font-medium text-[15px]">{editData.dob_year || "2005"}</div>
                      </div>
                    </div>

                    {/* iOS Style Wheel Picker Modal */}
                    <AnimatePresence>
                      {isWheelPickerOpen && (
                        <motion.div 
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center p-4 bg-black/40 backdrop-blur-sm"
                          onClick={() => setIsWheelPickerOpen(false)}
                        >
                          <motion.div 
                            initial={{ y: "100%" }}
                            animate={{ y: 0 }}
                            exit={{ y: "100%" }}
                            onClick={(e) => e.stopPropagation()}
                            className={`relative w-full max-w-[400px] rounded-[32px] overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.5)] border backdrop-blur-3xl ${
                              isDark ? 'bg-black border-blue-500/25 text-white' : 'bg-white border-blue-200 text-black'
                            }`}
                          >
                            {/* Blue Glows */}
                            <div className="absolute top-0 right-0 w-40 h-40 bg-blue-600/15 blur-[80px] pointer-events-none" />
                            <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-500/10 blur-[60px] pointer-events-none" />
                            <div className={`flex justify-between items-center px-6 py-4 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                              <button onClick={() => setIsWheelPickerOpen(false)} className="text-blue-500 font-medium">Cancel</button>
                              <span className="font-bold">Select Birthday</span>
                              <button 
                                onClick={() => {
                                  setEditData({
                                    ...editData,
                                    dob_day: tempDateSelection.day.toString(),
                                    dob_month: tempDateSelection.month,
                                    dob_year: tempDateSelection.year.toString()
                                  });
                                  setIsWheelPickerOpen(false);
                                }} 
                                className="text-blue-500 font-bold"
                              >
                                Done
                              </button>
                            </div>
                            
                            <div className="relative h-[240px] flex items-center justify-center overflow-hidden px-4">
                              {/* Selection Bars */}
                              <div className="absolute top-1/2 -translate-y-1/2 w-full h-[44px] pointer-events-none border-y border-gray-500/20" />
                              
                              <div className="flex w-full h-full relative">
                                {/* Day Column */}
                                <Column 
                                  items={[...Array(31)].map((_, i) => i + 1)} 
                                  value={tempDateSelection.day} 
                                  onChange={(d) => setTempDateSelection({...tempDateSelection, day: d})}
                                  isDark={isDark}
                                  width="flex-1"
                                />
                                {/* Month Column */}
                                <Column 
                                  items={["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]} 
                                  value={tempDateSelection.month} 
                                  onChange={(m) => setTempDateSelection({...tempDateSelection, month: m})}
                                  isDark={isDark}
                                  width="flex-[2]"
                                />
                                {/* Year Column */}
                                <Column 
                                  items={[...Array(100)].map((_, i) => 2026 - i)} 
                                  value={tempDateSelection.year} 
                                  onChange={(y) => setTempDateSelection({...tempDateSelection, year: y})}
                                  isDark={isDark}
                                  width="flex-[1.5]"
                                />
                              </div>

                              {/* Overlays for fade effect */}
                              <div className={`absolute top-0 left-0 w-full h-[80px] pointer-events-none bg-gradient-to-b ${ isDark ? 'from-black to-transparent' : 'from-white to-transparent' }`} />
                              <div className={`absolute bottom-0 left-0 w-full h-[80px] pointer-events-none bg-gradient-to-t ${ isDark ? 'from-black to-transparent' : 'from-white to-transparent' }`} />
                            </div>
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                )}
                
                {editingItem === 'country' && (
                   <div className="w-full">
                      {/* Trigger Input */}
                      <div 
                        onClick={() => {
                          setTempCountrySelection(editData.country_region || "India");
                          setIsCountryWheelPickerOpen(true);
                        }}
                        className={`border ${isDark ? 'border-white/10' : 'border-gray-200'} rounded-[32px] p-3 w-1/2 mx-auto cursor-pointer hover:bg-gray-50/5 transition-colors`}
                      >
                         <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1 px-4">
                           Country / Region
                         </label>
                         <div className="w-full bg-transparent outline-none font-medium text-[16px] px-4 py-1 flex justify-between items-center text-left">
                           <span>{editData.country_region || "Select Country"}</span>
                           <div className="flex flex-col -space-y-1 opacity-50">
                              <ChevronDown size={14} className="rotate-180" />
                              <ChevronDown size={14} />
                           </div>
                         </div>
                      </div>

                      {/* iOS Style Wheel Picker Modal for Country */}
                      <AnimatePresence>
                        {isCountryWheelPickerOpen && (
                          <motion.div 
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center p-4 bg-black/40 backdrop-blur-sm"
                            onClick={() => setIsCountryWheelPickerOpen(false)}
                          >
                            <motion.div 
                              initial={{ y: "100%" }}
                              animate={{ y: 0 }}
                              exit={{ y: "100%" }}
                              onClick={(e) => e.stopPropagation()}
                              className={`relative w-full max-w-[400px] rounded-[32px] overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.5)] border backdrop-blur-3xl ${
                                isDark ? 'bg-black border-blue-500/25 text-white' : 'bg-white border-blue-200 text-black'
                              }`}
                            >
                              {/* Blue Glows */}
                              <div className="absolute top-0 right-0 w-40 h-40 bg-blue-600/15 blur-[80px] pointer-events-none" />
                              <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-500/10 blur-[60px] pointer-events-none" />
                              <div className={`grid grid-cols-3 items-center px-6 py-4 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                                <button type="button" onClick={() => setIsCountryWheelPickerOpen(false)} className="text-blue-500 font-medium justify-self-start">Cancel</button>
                                <span className="font-bold whitespace-nowrap justify-self-center text-center">Country / Region</span>
                                <button 
                                  type="button"
                                  onClick={() => {
                                    setEditData({...editData, country_region: tempCountrySelection});
                                    setIsCountryWheelPickerOpen(false);
                                  }} 
                                  className="text-blue-500 font-bold justify-self-end"
                                >
                                  Done
                                </button>
                              </div>
                              
                              <div className="relative h-[240px] flex items-center justify-center overflow-hidden px-4">
                                <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none text-blue-500 opacity-10">
                                  <Globe size={120} strokeWidth={1} />
                                </span>

                                {/* Selection Bars */}
                                <div className="absolute top-1/2 -translate-y-1/2 w-full h-[44px] pointer-events-none border-y border-gray-500/20" />
                                
                                <div className="flex w-full h-full relative z-20">
                                  {/* Country Column */}
                                  <Column 
                                    items={countries.map(c => c.name)} 
                                    value={tempCountrySelection} 
                                    onChange={(c) => setTempCountrySelection(c)}
                                    isDark={isDark}
                                    width="flex-1"
                                  />
                                </div>

                                {/* Overlays for fade effect */}
                                <div className={`absolute top-0 left-0 w-full h-[80px] pointer-events-none bg-gradient-to-b ${ isDark ? 'from-black to-transparent' : 'from-white to-transparent' } z-30`} />
                                <div className={`absolute bottom-0 left-0 w-full h-[80px] pointer-events-none bg-gradient-to-t ${ isDark ? 'from-black to-transparent' : 'from-white to-transparent' } z-30`} />
                              </div>
                            </motion.div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                   </div>
                )}

                {editingItem === 'language' && (
                  <div className="w-full">
                     {/* Trigger Input */}
                     <div 
                       onClick={() => {
                         setTempLanguageSelection(editData.language || "English");
                         setIsLanguageWheelPickerOpen(true);
                       }}
                       className={`border ${isDark ? 'border-white/10' : 'border-gray-200'} rounded-[32px] p-3 w-1/2 mx-auto cursor-pointer hover:bg-gray-50/5 transition-colors`}
                     >
                        <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1 px-4">
                          Language
                        </label>
                        <div className="w-full bg-transparent outline-none font-medium text-[16px] px-4 py-1 flex justify-between items-center text-left">
                          <span>{editData.language || "Select Language"}</span>
                          <div className="flex flex-col -space-y-1 opacity-50">
                             <ChevronDown size={14} className="rotate-180" />
                             <ChevronDown size={14} />
                          </div>
                        </div>
                     </div>

                     {/* iOS Style Wheel Picker Modal for Language */}
                     <AnimatePresence>
                       {isLanguageWheelPickerOpen && (
                         <motion.div 
                           initial={{ opacity: 0 }}
                           animate={{ opacity: 1 }}
                           exit={{ opacity: 0 }}
                           className="fixed inset-0 z-[100] flex items-end justify-center sm:items-center p-4 bg-black/40 backdrop-blur-sm"
                           onClick={() => setIsLanguageWheelPickerOpen(false)}
                         >
                           <motion.div 
                             initial={{ y: "100%" }}
                             animate={{ y: 0 }}
                             exit={{ y: "100%" }}
                             onClick={(e) => e.stopPropagation()}
                             className={`relative w-full max-w-[400px] rounded-[32px] overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.5)] border backdrop-blur-3xl ${isDark ? 'bg-black border-blue-500/25 text-white' : 'bg-white border-blue-200 text-black'}`}
                           >
                             <div className={`grid grid-cols-3 items-center px-6 py-4 border-b ${isDark ? 'border-white/5' : 'border-gray-100'}`}>
                               <button type="button" onClick={() => setIsLanguageWheelPickerOpen(false)} className="text-blue-500 font-medium justify-self-start">Cancel</button>
                               <span className="font-bold whitespace-nowrap justify-self-center text-center">Language</span>
                               <button 
                                 type="button"
                                 onClick={() => {
                                   setEditData({...editData, language: tempLanguageSelection});
                                   setIsLanguageWheelPickerOpen(false);
                                 }} 
                                 className="text-blue-500 font-bold justify-self-end"
                               >
                                 Done
                               </button>
                             </div>
                             
                             <div className="relative h-[240px] flex items-center justify-center overflow-hidden px-4">
                               <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 pointer-events-none text-blue-500 opacity-10">
                                 <Globe size={120} strokeWidth={1} />
                               </span>

                               {/* Selection Bars */}
                               <div className="absolute top-1/2 -translate-y-1/2 w-full h-[44px] pointer-events-none border-y border-gray-500/20" />
                               
                               <div className="flex w-full h-full relative z-20">
                                 {/* Language Column */}
                                 <Column 
                                   items={languages} 
                                   value={tempLanguageSelection} 
                                   onChange={(l) => setTempLanguageSelection(l)}
                                   isDark={isDark}
                                   width="flex-1"
                                 />
                               </div>

                               {/* Overlays for fade effect */}
                               <div className={`absolute top-0 left-0 w-full h-[80px] pointer-events-none bg-gradient-to-b ${isDark ? 'from-[#1c1c1e] to-transparent' : 'from-white to-transparent'} z-30`} />
                               <div className={`absolute bottom-0 left-0 w-full h-[80px] pointer-events-none bg-gradient-to-t ${isDark ? 'from-[#1c1c1e] to-transparent' : 'from-white to-transparent'} z-30`} />
                             </div>
                           </motion.div>
                         </motion.div>
                       )}
                     </AnimatePresence>
                  </div>
                )}
              </div>
            </div>

              <div className={`px-8 pb-8 flex flex-col items-center ${editingItem === 'name' ? 'gap-3 mt-4' : 'gap-4 mt-6'}`}>
                <button 
                  disabled={isSaving}
                  onClick={async () => {
                    // Card payment redirect
                    if (editingItem === 'new_payment' && editData.payment_type === 'Card') {
                       setPaymentConfig({ allowedMethods: ['Card'], amount: "2.00", productinfo: "Add Card Verification" });
                       setEditingItem(null);
                       setShowFozzilPayModal(true);
                       return;
                    }

                    // Password: send reset email via Supabase
                    if (editingItem === 'password') {
                      if (passwordResetSent) { setEditingItem(null); setPasswordResetSent(false); return; }
                      setIsSaving(true);
                      setPasswordResetError('');
                      const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
                        redirectTo: `${window.location.origin}/account`
                      });
                      setIsSaving(false);
                      if (error) {
                        setPasswordResetError(error.message || 'Failed to send reset email. Try again.');
                      } else {
                        setPasswordResetSent(true);
                        // Optional: notify that a reset was initiated
                      }
                      return;
                    }

                    // Security: save 2FA toggle to profile
                    if (editingItem === 'security') {
                      setIsSaving(true);
                      const { error } = await supabase.from('profiles').update({ two_factor_auth: editData.two_factor_auth }).eq('id', user.id);
                      if (!error) {
                        const mfaType = editData.two_factor_auth ? 'mfa_added' : 'mfa_removed';
                        notifySecurityChange(mfaType);
                        setProfile({...profile, two_factor_auth: editData.two_factor_auth});
                        setSecuritySaveMsg('Security settings saved!');
                        setTimeout(() => { setSecuritySaveMsg(''); setEditingItem(null); }, 1200);
                      }
                      setIsSaving(false);
                      return;
                    }

                    // Recovery: save recovery_contact + recovery_setup
                    if (editingItem === 'recovery') {
                      setRecoveryError('');
                      // Validate email if provided
                      const email = (editData.recovery_contact || '').trim();
                      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                        setRecoveryError('Please enter a valid email address.');
                        return;
                      }
                      if (email && email === user.email) {
                        setRecoveryError('Recovery contact cannot be your own email.');
                        return;
                      }
                      setIsSaving(true);
                      // Auto-enable recovery_setup if contact is provided
                      const recoverySetup = email ? true : (editData.recovery_setup || false);
                      const { error } = await supabase.from('profiles').update({
                        recovery_contact: email || null,
                        recovery_setup: recoverySetup
                      }).eq('id', user.id);
                      if (!error) {
                        setProfile({...profile, recovery_contact: email || null, recovery_setup: recoverySetup});
                        setEditData(prev => ({...prev, _recoveryContactSaved: !!email, recovery_setup: recoverySetup}));
                        setRecoverySaveMsg(email ? 'Recovery contact saved successfully!' : 'Recovery settings updated.');
                        setTimeout(() => { setRecoverySaveMsg(''); setEditingItem(null); }, 1500);
                      } else {
                        setRecoveryError(error.message || 'Failed to save. Please try again.');
                      }
                      setIsSaving(false);
                      return;
                    }

                    // Legacy: save legacy_contact
                    if (editingItem === 'legacy') {
                      setLegacyError('');
                      const email = (editData.legacy_contact || '').trim();
                      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                        setLegacyError('Please enter a valid email address.');
                        return;
                      }
                      if (email && email === user.email) {
                        setLegacyError('Legacy contact cannot be your own email.');
                        return;
                      }
                      setIsSaving(true);
                      const { error } = await supabase.from('profiles').update({
                        legacy_contact: email || null
                      }).eq('id', user.id);
                      if (!error) {
                        setProfile({...profile, legacy_contact: email || null});
                        setEditData(prev => ({...prev, _legacyContactSaved: !!email}));
                        setLegacySaveMsg(email ? 'Legacy contact saved successfully!' : 'Legacy contact removed.');
                        setTimeout(() => { setLegacySaveMsg(''); setEditingItem(null); }, 1500);
                      } else {
                        setLegacyError(error.message || 'Failed to save. Please try again.');
                      }
                      setIsSaving(false);
                      return;
                    }

                    if (editingItem === 'email_phone') {
                       // Custom handler for combined email/phone
                       setIsSaving(true);
                       const { phoneCode, phoneNumber } = editData;
                       const cleanNum = phoneNumber.replace(/[^\d]/g, '');
                       
                       const combinedPhone = `${phoneCode}${cleanNum}`;
                       
                       const { error } = await supabase.from('profiles').update({ 
                         phone: combinedPhone,
                         country_region: editData.country_region 
                       }).eq('id', user.id);
                       
                       if (!error) {
                         // Only notify if phone actually changed or was added
                         if (profile?.phone !== combinedPhone) {
                           notifySecurityChange('phone_changed');
                         }
                         setProfile({...profile, phone: combinedPhone, country_region: editData.country_region});
                         setEditingItem(null);
                       }
                       setIsSaving(false);
                       return;
                    }

                    // Default: save all editData fields to profile
                    setIsSaving(true);
                    const { showPhoneInput, phoneCode, phoneNumber, isPhoneLocked, ...dataToSave } = editData;
                    const { error } = await supabase.from('profiles').update(dataToSave).eq('id', user.id);
                    if (!error) {
                      setProfile({...profile, ...editData});
                      setEditingItem(null);
                    }
                    setIsSaving(false);
                  }}
                  className={`w-[180px] py-2.5 rounded-full font-black tracking-widest transition-all text-xs shadow-[0_12px_40px_rgba(59,130,246,0.3)] border-t border-white/20 ${
                    isSaving
                      ? 'bg-gray-400 cursor-not-allowed text-white'
                      : isDark 
                        ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white hover:scale-105 active:scale-95'
                        : 'bg-blue-600 text-white hover:bg-blue-500 hover:scale-105 active:scale-95'
                  }`}
                >
                  {isSaving ? "Saving..." : (
                    editingItem === 'password' ? (passwordResetSent ? 'Close' : 'Send Reset Email') :
                    editingItem === 'recovery' ? (editData.recovery_contact && !editData._recoveryContactSaved ? 'Save Recovery Contact' : 'Done') :
                    editingItem === 'legacy' ? (editData.legacy_contact && !editData._legacyContactSaved ? 'Save Legacy Contact' : 'Done') :
                    editingItem === 'security' ? 'Save' :
                    ['name', 'dob', 'language', 'country'].includes(editingItem) ? 'Save' : 'Done'
                  )}
                </button>


              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Saver Modal (Step 1) */}
      <FozzilPayModal 
        isOpen={showFozzilPayModal}
        onClose={() => setShowFozzilPayModal(false)}
        onSuccess={async (newMethod) => {
          // Refresh payment methods list
          try {
            const methodsResult = await FozzilPay.getPaymentMethods(user.id);
            if (methodsResult.success && methodsResult.data) {
              setPaymentMethods(methodsResult.data);
            }
          } catch (err) {
            console.error('Error refreshing payment methods:', err);
          }
        }}
        user={user}
        user_id={user?.id}
        mode="save_method"
        onSwitchToCheckout={() => {
           setShowFozzilPayModal(false);
           setShowCheckoutModal(true);
        }}
      />

      {/* Checkout Modal (Step 2 - Only for Cards) */}
      <FozzilPayCheckout 
        isOpen={showCheckoutModal}
        onClose={() => setShowCheckoutModal(false)}
        onSuccess={async (newMethod) => {
          // Refresh payment methods list
          try {
            const methodsResult = await FozzilPay.getPaymentMethods(user.id);
            if (methodsResult.success && methodsResult.data) {
              setPaymentMethods(methodsResult.data);
            }
          } catch (err) {
            console.error('Error refreshing payment methods:', err);
          }
        }}
        user={user}
        user_id={user?.id}
        amount="2.00"
        productinfo="Add Card Verification"
        allowedMethods={['Card']}
        isRecurring={false}
        firstname={user?.user_metadata?.full_name || user?.email?.split('@')[0]}
        email={user?.email}
      />

      {/* Billing Address Modal */}
      <BillingAddressModal
        isOpen={showBillingModal}
        onClose={() => {
          setShowBillingModal(false);
          setEditingAddress(null);
        }}
        onSuccess={async (updatedAddress) => {
          // Refresh profile data and secondary addresses
          try {
            const { data: updatedProfile } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', user.id)
              .single();
            if (updatedProfile) {
              setProfile(updatedProfile);
            }
            
            // Also refresh secondary addresses
            const addressResult = await FozzilPay.getAddresses(user.id);
            if (addressResult.success && addressResult.data) {
              setSecondaryAddresses(addressResult.data);
            }
          } catch (err) {
            console.error('Error refreshing data:', err);
          }
        }}
        user={user}
        profile={profile}
        editingAddress={editingAddress}
      />

      {/* Device Detail Modal */}
      <DeviceDetailModal
        isOpen={showDeviceModal}
        onClose={() => {
          setShowDeviceModal(false);
          setSelectedDevice(null);
        }}
        device={selectedDevice}
        onDeviceRemoved={async (deviceId, justRefresh = false) => {
          if (justRefresh) {
            // Refresh device list
            const deviceData = await getUserDevices(user.id);
            setDevices(deviceData);
          } else {
            // Remove from local state
            setDevices(prev => prev.filter(d => d.id !== deviceId));
          }
        }}
        onSignOut={async () => {
          await cleanupDeviceSession();
          logout();
        }}
        theme={theme}
      />

      {/* Trust This Device Prompt */}
      <AnimatePresence>
        {showTrustPrompt && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-md"
              onClick={() => setShowTrustPrompt(false)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.92, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.92, y: 40 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className={`relative w-full max-w-[420px] rounded-[32px] overflow-hidden shadow-[0_24px_64px_rgba(0,0,0,0.5)] border backdrop-blur-3xl p-8 text-center ${
                isDark ? 'bg-black text-white border-blue-500/25' : 'bg-white text-black border-blue-200'
              }`}
            >
              {/* Blue Glows */}
              <div className="absolute top-0 right-0 w-48 h-48 bg-blue-600/15 blur-[80px] pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-blue-500/10 blur-[60px] pointer-events-none" />

              {/* Shield Icon */}
              <div className="relative z-10">
                <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                  <Shield size={28} className="text-blue-500" />
                </div>

                <h3 className="text-xl font-bold mb-1">Trust This Device?</h3>
                <p className={`text-[13px] mb-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  You're signing in from a new device. Would you like to add it as a trusted device for your FOZZIL account?
                </p>

                {/* Device Info */}
                <div className={`p-4 rounded-[32px] mb-6 border ${isDark ? 'bg-white/[0.03] border-white/5' : 'bg-gray-50/50 border-gray-100'}`}>
                  <div className="flex items-center gap-3 justify-center">
                    {(() => {
                      const currentId = getCurrentDeviceId();
                      const currentDev = devices.find(d => d.id === currentId);
                      if (!currentDev) return <Laptop size={20} className="text-blue-500" />;
                      return (
                        <>
                          {currentDev.device_type === 'mobile' || currentDev.device_type === 'tablet' 
                            ? <Smartphone size={20} className="text-blue-500" /> 
                            : <Laptop size={20} className="text-blue-500" />}
                          <div className="text-left">
                            <p className={`text-[14px] font-bold ${isDark ? 'text-white' : 'text-black'}`}>{currentDev.device_name}</p>
                            <p className={`text-[12px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{currentDev.os} {currentDev.os_version}</p>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={() => setShowTrustPrompt(false)}
                    className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all border ${
                      isDark 
                        ? 'bg-white/5 text-white hover:bg-white/10 border-white/10' 
                        : 'bg-gray-100 text-black hover:bg-gray-200 border-gray-200'
                    }`}
                  >
                    Not Now
                  </button>
                  <button
                    onClick={async () => {
                      const currentId = getCurrentDeviceId();
                      if (currentId) {
                        await trustDevice(currentId);
                        const deviceData = await getUserDevices(user.id);
                        setDevices(deviceData);
                      }
                      setShowTrustPrompt(false);
                    }}
                    className="px-6 py-2.5 rounded-full text-sm font-bold transition-all bg-[#065DC0] hover:bg-[#0071e3] text-white hover:scale-105 active:scale-95 shadow-lg"
                  >
                    Trust Device
                  </button>
                </div>

                <p className={`text-[11px] mt-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                  Trusted devices appear in your security settings and can receive verification codes.
                </p>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

    </div>
  );
};

// iOS Style Wheel Picker Column Component
// iOS Style Wheel Picker Column Component
const Column = ({ items, value, onChange, isDark, width }) => {
  const containerRef = React.useRef(null);
  const audioContextRef = React.useRef(null);
  const itemHeight = 44;
  const paddingHeight = 98; // Height to center the item

  React.useEffect(() => {
    // Initialize Audio Context on mount
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    
    // Cleanup
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  const playClickSound = React.useCallback(() => {
    if (audioContextRef.current) {
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.setValueAtTime(800, audioContextRef.current.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(1200, audioContextRef.current.currentTime + 0.02);
      
      gainNode.gain.setValueAtTime(0.05, audioContextRef.current.currentTime); // Lower volume for subtlety
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContextRef.current.currentTime + 0.03);

      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);

      oscillator.start();
      oscillator.stop(audioContextRef.current.currentTime + 0.03);
    }
  }, []);

  const isInternalChange = React.useRef(false);
  const isMounted = React.useRef(false);

  React.useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    
    const index = items.indexOf(value);
    if (index !== -1 && containerRef.current) {
      if (!isMounted.current) {
        containerRef.current.scrollTop = index * itemHeight;
        isMounted.current = true;
      } else {
        containerRef.current.scrollTo({
          top: index * itemHeight,
          behavior: 'smooth'
        });
      }
    }
  }, [value, items, itemHeight]);

  const handleScroll = (e) => {
    const scrollTop = e.target.scrollTop;
    const index = Math.round(scrollTop / itemHeight);
    if (items[index] !== undefined && items[index] !== value) {
      isInternalChange.current = true;
      onChange(items[index]);
      playClickSound();
    }
  };

  return (
    <>
      <style>{`
        .hide-scroll-cross::-webkit-scrollbar {
          display: none !important;
          width: 0 !important;
          height: 0 !important;
        }
      `}</style>
      <div 
        className={`relative h-full overflow-y-auto no-scrollbar hide-scroll-cross snap-y snap-mandatory ${width}`} 
        ref={containerRef} 
        onScroll={handleScroll}
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <div style={{ height: paddingHeight }} />
        {items.map((item, i) => {
          const index = items.indexOf(value);
          const distance = Math.abs(i - index);
          const opacity = Math.max(0.3, 1 - distance * 0.25);
          const scale = Math.max(0.8, 1 - distance * 0.05);
          const rotate = (i - index) * 15; // 3D Tilt effect

          return (
            <div 
              key={i} 
              className="h-[44px] flex items-center justify-center snap-center transition-all duration-100 px-2"
              style={{ 
                opacity,
                transform: `scale(${scale}) rotateX(${rotate}deg)`,
                color: i === index ? (isDark ? 'white' : 'black') : (isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'),
                fontWeight: i === index ? 'bold' : 'normal',
                fontSize: i === index ? '20px' : '17px'
              }}
            >
              {item}
            </div>
          );
        })}
        <div style={{ height: paddingHeight }} />
      </div>
    </>
  );
};



export default Account;
